import { DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import EmployeeAvatar from "../../../components/EmployeeAvatar";
import Toasts from "../../../core/common/toast/index";
import { API_URLS, BASE_URLS } from "../../../core/data/redux/apis/urls";
import {
  ChatMessage,
  clearError,
  clearMessages,
  clearUploadError,
  fetchMessages,
  fetchUnreadCount,
  fetchChatAttachments,
  sendMessage as sendChatMessage,
  uploadChatFiles,
} from "../../../core/data/redux/nocOutsideJobChatSlice";
import {
  openChatAttachment,
  openChatAttachmentInExternalView,
  downloadChatAttachment,
  prepareUploadedAttachments,
  getValidNocType,
} from "../../../utils/nocChatUtils";
import {
  clearNocData,
  clearNocError,
  fetchNocData,
  fetchNocDnARDropdownData,
} from "../../../core/data/redux/sapWorklistSlice";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
} from "../../../utils/fileUtils";
import { getRequestStatusLabel } from "../../../core/data/constants/requestStatus";

interface TaskDetailsPageProps { }


const TaskDetailsPage: React.FC<TaskDetailsPageProps> = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  // Redux state
  const { nocData, nocDropdownData, nocLoading, nocError } = useSelector(
    (state: any) => state.sapWorklistState
  );
  const user = useSelector(
    (state: any) => state.employeeAuthState?.user || state.authState?.user
  );

  // Get NOC type from URL params or default to NOC_OUTJOB
  const nocType =
    (searchParams.get("type") as
      | "NOC_OUTJOB"
      | "NOC_EXINDIA"
      | "NOC_HIGHERSTUDY"
      | "NOC_PASSPORT"
      | "NOC_ONLINECOURSE-REIMB"
      | "NOC_ONLINECOURSE") || "NOC_OUTJOB";
  const requestID = id;

  const [approvedAmount, setApprovedAmount] = useState<string>("");
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  const formatEmpIdForDisplay = (id: any) => {
    if (!id) return "";
    const s = String(id).trim();
    const num = parseInt(s, 10);
    return isNaN(num) ? s : String(num);
  };

  const formatStatusText = (status: string | undefined | null): string => {
    if (!status) return "Pending";
    const s = String(status).trim();
    const upper = s.toUpperCase();
    if (upper === "T" || upper === "TO BE APPROVED") return "To Be Approved";
    if (upper === "A" || upper === "APPROVED") return "Approved";
    if (upper === "R" || upper === "REJECTED") return "Rejected";
    if (upper === "P" || upper === "PENDING" || upper === "PARTIALLY APPROVED") return "Partially Approved";
    if (upper === "D" || upper === "PROCESSING COMPLETE") return "Processing Complete";
    if (upper === "N" || upper === "NEW") return "New";
    if (upper === "K" || upper === "CANCELLED") return "Cancelled";
    if (upper === "X" || upper === "DELETED") return "Deleted";
    if (upper === "B" || upper === "TO BE PROCESSED") return "To Be Processed";
    if (upper === "C" || upper === "CLOSED") return "Closed";
    if (upper === "S" || upper === "SETTLED") return "Settled";
    return getRequestStatusLabel(upper) || s;
  };

  const formatStatusBadgeClass = (status: string | undefined | null): string => {
    if (!status) return "badge bg-warning text-dark";
    const s = String(status).trim();
    const upper = s.toUpperCase();
    if (
      upper === "T" ||
      upper === "TO BE APPROVED" ||
      upper === "PENDING" ||
      upper === "P" ||
      upper === "PARTIALLY APPROVED" ||
      upper === "B" ||
      upper === "TO BE PROCESSED"
    ) {
      return "badge bg-warning text-dark";
    }
    if (
      upper === "A" ||
      upper === "APPROVED" ||
      upper === "D" ||
      upper === "PROCESSING COMPLETE" ||
      upper === "S" ||
      upper === "SETTLED"
    ) {
      return "badge bg-success";
    }
    if (
      upper === "R" ||
      upper === "REJECTED" ||
      upper === "K" ||
      upper === "CANCELLED" ||
      upper === "X" ||
      upper === "DELETED"
    ) {
      return "badge bg-danger";
    }
    if (upper === "N" || upper === "NEW") {
      return "badge bg-info text-dark";
    }
    return "badge bg-secondary";
  };

  const formatDarStatus = (status: string | undefined | null) => {
    if (!status) return "N/A";
    const s = String(status).trim();
    const upper = s.toUpperCase();
    if (upper === "N" || upper === "1") return "No D&AR contemplated against the employee";
    if (upper === "P" || upper === "2" || upper.includes("DEEMED SUSPENSION")) return "D&AR Pending/ Deemed Suspension /Suspension/ Court Case Pending";
    if (upper === "M" || upper === "3") return "D&AR completed; Minor penalty has been imposed";
    if (upper === "J" || upper === "4") return "D&AR completed; Major penalty has been imposed";
    return s;
  };

  const formatNatureOfDuty = (val: string | undefined | null, dutyObj?: any) => {
    if (dutyObj?.nature_of_duty_title) return dutyObj.nature_of_duty_title;
    if (!val) return "N/A";
    const str = String(val).trim();
    const upper = str.toUpperCase();
    if (upper === "G" || upper === "GENERAL") return "General";
    if (upper === "S" || upper === "SHIFT") return "Shift";
    if (upper === "R" || upper === "REGULAR") return "Regular";
    if (upper === "E" || upper === "EXECUTIVE") return "Executive";
    if (upper === "01") return "Regular";
    if (upper === "02") return "Shift";
    return str;
  };

  const formatCourseType = (data: any) => {
    if (data?.course_type?.course_type_title) return data.course_type.course_type_title;
    const val = data?.diplomaOrDegree || data?.typeOfCourse;
    if (!val) return data?.otherThanDiploma && data?.otherThanDiploma !== "other" ? data.otherThanDiploma : "N/A";
    const str = String(val).trim();
    const upper = str.toUpperCase();
    if (upper === "P" || upper === "POST" || upper === "POST GRADUATION") return "Post Graduation";
    if (upper === "D" || upper === "DIPLOMA") return "Diploma";
    if (upper === "G" || upper === "DEGREE" || upper === "GRADUATION") return "Graduation";
    if (upper === "O" || upper === "OTHER") return data?.otherThanDiploma && data.otherThanDiploma !== "other" ? data.otherThanDiploma : "Other";
    return str;
  };

  const formatCourseMode = (data: any) => {
    if (data?.mode_of_course?.course_mode_title) return data.mode_of_course.course_mode_title;
    const val = data?.partTimeOrCorresp || data?.modeOfCourse;
    if (!val) return "N/A";
    const str = String(val).trim();
    const upper = str.toUpperCase();
    if (upper === "C" || upper === "CORRESPONDENCE") return "Correspondence";
    if (upper === "P" || upper === "PARTTIME" || upper === "PART TIME") return "Part Time";
    if (upper === "F" || upper === "FULLTIME" || upper === "FULL TIME") return "Full Time";
    if (upper === "R" || upper === "REGULAR") return "Regular";
    return str;
  };

  const formatCourseLocation = (val: string | undefined | null) => {
    if (!val) return "India";
    const str = String(val).trim();
    const upper = str.toUpperCase();
    if (upper === "I" || upper === "Y" || upper === "INDIA") return "India";
    if (upper === "O" || upper === "N" || upper === "OUTSIDE INDIA") return "Outside India";
    return str;
  };

  useEffect(() => {
    console.log("nocType:>>>>", nocData);
    const initialStatus =
      nocData?.dnARStatus ||
      nocData?.dandARStatus ||
      nocData?.DandARStatus ||
      nocData?.DnARStatus ||
      "";
    const rawPenaltyDate =
      nocData?.dnARPenaltyEffecti ||
      nocData?.dandARPenaltyEffective ||
      nocData?.DandARPenaltyEffective ||
      nocData?.DnARPenaltyEffecti ||
      "";

    const initialPenaltyDate =
      rawPenaltyDate &&
        rawPenaltyDate !== "0000-00-00" &&
        rawPenaltyDate !== "00000000" &&
        rawPenaltyDate !== "00.00.0000" &&
        dayjs(rawPenaltyDate).isValid()
        ? rawPenaltyDate
        : "";

    if (initialStatus) {
      setDnARStatus(initialStatus);
    }
    if (initialPenaltyDate) {
      setDnARPenaltyEffective(initialPenaltyDate);
    }

    const initialRemarks =
      (nocData?.Remarks !== "X" && nocData?.Remarks) ||
      (nocData?.remarks !== "X" && nocData?.remarks) ||
      (nocData?.Remarks1 !== "X" && nocData?.Remarks1) ||
      (nocData?.remarks1 !== "X" && nocData?.remarks1) ||
      "";

    setRemarks(initialRemarks);
    setHRemarks("");

    if (nocType == "NOC_ONLINECOURSE" || nocType == "NOC_ONLINECOURSE-REIMB") {
      const isDeclAccepted =
        nocData?.declarationAccepted === true ||
        nocData?.declarationAccepted === "X" ||
        nocData?.declarationAccepted === "Y" ||
        nocData?.declaration_accepted === true ||
        nocData?.declaration_accepted === "X" ||
        nocData?.declaration === true ||
        nocData?.declaration === "X" ||
        Boolean(nocData?.id || nocData?.requestId || nocData?.requestID);
      setDeclarationAccepted(isDeclAccepted);
      if (nocType == "NOC_ONLINECOURSE-REIMB") {
        setApprovedAmount(nocData?.approvedAmount || "");
      }
    }
  }, [nocData, nocType]);

  // Get chat data from Redux state with custom equality to prevent heavy re-renders during background polling
  const {
    messages,
    attachments,
    loading: chatLoading,
    unreadCount,
    error: chatError,
    uploading,
    uploadError,
  } = useSelector((state: any) => state.nocChat, (prev, next) => {
    if (prev.error !== next.error) return false;
    if (prev.uploading !== next.uploading) return false;
    if (prev.uploadError !== next.uploadError) return false;
    if (prev.unreadCount !== next.unreadCount) return false;
    if (prev.attachments?.length !== next.attachments?.length) return false;

    // Compare messages array
    if (prev.messages?.length !== next.messages?.length) return false;
    if (prev.messages?.length > 0 && next.messages?.length > 0) {
      if (prev.messages[prev.messages.length - 1]?.id !== next.messages[next.messages.length - 1]?.id) {
        return false;
      }
    }

    // Allow loading state to trigger re-render ONLY during initial load (when no messages exist).
    // This ignores background polling loading toggles, which otherwise freeze the 5000-line component.
    if ((!prev.messages || prev.messages.length === 0) && prev.loading !== next.loading) {
      return false;
    }

    return true; // No relevant changes, skip re-render
  });

  const rawType = (searchParams.get("type") || "").toLowerCase();

  useEffect(() => {
    if (rawType.includes("sap_authorization") || rawType.includes("sap_auth") || rawType === "sap_authorization_form") {
      navigate(`/sap-authorization/approval/${id}`);
    }
    if (rawType === "sv4" || rawType.includes("sv4") || rawType.includes("smart_validator")) {
      navigate(`/sv4-card/approve/${encodeURIComponent(String(id || ""))}`);
    }
    if (rawType.includes("self_transfer") || rawType.includes("transfer_posting") || rawType === "transfer") {
      navigate(`/todo-worklist/transfer-posting-approval/${encodeURIComponent(String(id || ""))}`);
    }
  }, [rawType, id, navigate]);

  // Editable fields state
  const [editableFields, setEditableFields] = useState<{ [key: string]: any }>(
    {}
  );
  const [isEditing, setIsEditing] = useState(false);

  // Extract VPF data if applicable
  const { worklistData } = useSelector((state: any) => state.sapWorklistState);
  const isVpf = rawType.toUpperCase().includes("VPF");
  const vpfItem = isVpf ? worklistData?.data?.find((item: any) => item.id.toString() === id || item.requestID?.toString() === id) : null;

  // D&AR Status field state
  const [dnARStatus, setDnARStatus] = useState<string>("");
  const [dnARPenaltyEffective, setDnARPenaltyEffective] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [hRemarks, setHRemarks] = useState<string>("");
  const [declarationAccepted, setDeclarationAccepted] =
    useState<boolean>(false);
  const [isDownloadingNoc, setIsDownloadingNoc] = useState<boolean>(false);

  const handleDownloadNocPDF = async () => {
    if (!nocData || isDownloadingNoc) return;
    try {
      setIsDownloadingNoc(true);
      const sapTypeMap: Record<string, string> = {
        NOC_OUTJOB: "ZNOJ",
        NOC_EXINDIA: "ZNEX",
        NOC_HIGHERSTUDY: "ZHIS",
        NOC_PASSPORT: "ZNVP",
        NOC_ONLINECOURSE: "ZNOC",
      };
      const sapType = sapTypeMap[nocType] || "ZNOJ";
      const payload = {
        empId: nocData.empID || nocData.empId,
        type: sapType,
        referenceNumber: nocData.referenceNumber,
      };

      const res = await Api.post(
        API_URLS.NOC.DOWNLOAD_DOC,
        payload
      );

      const docItem = res?.data?.data?.[0] || res?.data;
      if (res?.data?.success && docItem?.url) {
        window.open(docItem.url, "_blank");
      } else {
        showToast("Error", res?.data?.message || "Failed to download NOC document.", "danger");
      }
    } catch (err: any) {
      console.error("NOC Download Error:", err);
      showToast("Error", err?.message || "Failed to download NOC document.", "danger");
    } finally {
      setIsDownloadingNoc(false);
    }
  };

  // Additional NOC_OUTJOB States
  const [rejectionReasons, setRejectionReasons] = useState<any[]>([]);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState<string>("");
  const [hrDocType, setHrDocType] = useState<string>("O");
  const [hrUploadedDocs, setHrUploadedDocs] = useState<any[]>([]);
  const [hrDocUploading, setHrDocUploading] = useState<boolean>(false);
  const [finalDandARVigilance, setFinalDandARVigilance] = useState<string>("");
  const [jobNatureOptions, setJobNatureOptions] = useState<any[]>([]);

  // Employee Education Data State (Mobile repo component alignment)
  const [empEduData, setEmpEduData] = useState<any[]>([]);
  const [isEmpEduLoading, setIsEmpEduLoading] = useState<boolean>(false);

  const fetchEmpEduData = async (targetEmpId: string) => {
    if (!targetEmpId) return;
    setIsEmpEduLoading(true);
    try {
      const res = await Api.post(API_URLS.NOC.GET_EMPLOYEE_EDUCATION, {
        empId: targetEmpId,
        data: "education",
      });
      if (res?.data?.success && res?.data?.data) {
        setEmpEduData(res.data.data.education ?? res.data.data ?? []);
      } else if (res?.data?.data) {
        setEmpEduData(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setEmpEduData([]);
      }
    } catch (err) {
      console.error("Error fetching employee education data:", err);
      setEmpEduData([]);
    } finally {
      setIsEmpEduLoading(false);
    }
  };

  useEffect(() => {
    const targetEmpId = nocData?.employee?.empId || nocData?.empID || nocData?.empId;
    if (targetEmpId && nocType === "NOC_OUTJOB") {
      fetchEmpEduData(targetEmpId);
    } else {
      setEmpEduData([]);
    }
  }, [nocData, nocType]);

  const [reimbParentCourse, setReimbParentCourse] = useState<any>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchReimbParentCourse = async () => {
      if (nocType !== "NOC_ONLINECOURSE-REIMB" || !nocData) return;
      if (nocData.online_course || nocData.noc_online_course) {
        if (isMounted) setReimbParentCourse(nocData.online_course || nocData.noc_online_course);
        return;
      }
      const refNo = nocData.referenceNumber1 || nocData.associatedNocRef;
      if (!refNo) return;

      try {
        let resolved: any = null;
        try {
          const res = await Api.post(API_URLS.NOC.FIND_ONE_ONLINE_COURSE, { referenceNumber: refNo });
          const d = res?.data?.data || res?.data;
          if (d && (d.id || d.onlineCourseName)) resolved = d;
        } catch { }

        if (!resolved) {
          try {
            const resAll = await Api.post(API_URLS.NOC.FIND_ALL_ONLINE_COURSES, { query: refNo });
            const list = resAll?.data?.data?.data || resAll?.data?.data || resAll?.data;
            if (Array.isArray(list) && list.length > 0) {
              resolved = list.find((c: any) => String(c.referenceNumber).trim() === String(refNo).trim() || String(c.id).trim() === String(refNo).trim()) || list[0];
            }
          } catch { }
        }

        if (resolved && resolved.id) {
          try {
            const single = await Api.post(API_URLS.NOC.FIND_ONE_ONLINE_COURSE, { id: resolved.id });
            const sd = single?.data?.data || single?.data;
            if (sd && (sd.id || sd.onlineCourseName)) resolved = sd;
          } catch { }
        }

        if (isMounted && resolved) {
          setReimbParentCourse(resolved);
        }
      } catch (e) {
        console.warn("Failed fetching parent course in TaskDetailsPage:", e);
      }
    };

    fetchReimbParentCourse();
    return () => {
      isMounted = false;
    };
  }, [nocData, nocType]);

  useEffect(() => {
    if (nocData) {
      setEditableFields(prev => ({
        postAppliedFor: prev.postAppliedFor ?? (nocData.postAppliedfor || nocData.PostAppliedFor || ""),
        organisationInWhich: prev.organisationInWhich ?? (nocData.organisationInWhich || nocData.organisation || nocData.OrganisationInWhich || ""),
        natureOfOutside: prev.natureOfOutside ?? (nocData.natureDetails?.natureId || nocData.natureOfOutside || ""),
        advertisementNo: prev.advertisementNo ?? (nocData.advertisementNo || nocData.advertisementNo1 || ""),
        advertisementDate: prev.advertisementDate ?? (nocData.advertisementDate || nocData.advertisementDate1 || ""),
        courseName: prev.courseName ?? (nocData.courseName || ""),
        institute: prev.institute ?? (nocData.institute || ""),
        partTimeOrCorresp: prev.partTimeOrCorresp ?? (nocData.partTimeOrCorresp || nocData.modeOfCourse || ""),
      }));
    }
  }, [nocData]);

  useEffect(() => {
    if (nocType === "NOC_OUTJOB") {
      fetchRejectionReasons();
      fetchJobNatures();
    }
  }, [nocType]);

  const fetchJobNatures = async () => {
    try {
      const res = await Api.post(`${BASE_URLS.BASE_URL}masters/outside-job-nature`, {});
      if (res?.data?.data && Array.isArray(res.data.data)) {
        setJobNatureOptions(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching job natures", err);
    }
  };

  const fetchRejectionReasons = async () => {
    try {
      const res = await Api.post(`${BASE_URLS.BASE_URL}noc/noc-outjob-reject-reasons`, {});
      if (res?.data?.data && Array.isArray(res.data.data)) {
        setRejectionReasons(res.data.data);
      } else {
        setRejectionReasons([
          { id: 1, reasonForRejection: "1", description: "Incomplete details or documentation" },
          { id: 2, reasonForRejection: "2", description: "Not eligible as per DMRC policy" },
          { id: 3, reasonForRejection: "3", description: "Under D&AR/Vigilance proceedings" },
          { id: 4, reasonForRejection: "4", description: "Other administrative reason" },
        ]);
      }
    } catch (err) {
      setRejectionReasons([
        { id: 1, reasonForRejection: "1", description: "Incomplete details or documentation" },
        { id: 2, reasonForRejection: "2", description: "Not eligible as per DMRC policy" },
        { id: 3, reasonForRejection: "3", description: "Under D&AR/Vigilance proceedings" },
        { id: 4, reasonForRejection: "4", description: "Other administrative reason" },
      ]);
    }
  };

  const handleDocumentClick = async (docParam: any) => {
    if (!docParam) {
      showToast("Warning", "Document file information is missing", "warning");
      return;
    }

    // Synchronously open a new tab to bypass browser popup blockers
    const popup = window.open("", "_blank");
    if (popup) {
      popup.document.title = "Loading document...";
      popup.document.body.innerHTML = `
        <div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;color:#555;">
          <div style="text-align:center;">
            <div style="font-size:18px;font-weight:600;margin-bottom:8px;">Loading document...</div>
            <div style="font-size:14px;color:#888;">Please wait while your file is prepared.</div>
          </div>
        </div>
      `;
    }

    try {
      // 1. Check if docParam has embedded base64 content
      const content =
        typeof docParam === "object"
          ? docParam.Content || docParam.content || docParam.FileContent || docParam.fileContent
          : null;

      if (content && typeof content === "string" && content.trim() !== "") {
        try {
          const cleanBase64 = content.trim().replace(/^data:.*?;base64,/, "").replace(/^data:.*?,/, "");
          const byteCharacters = atob(cleanBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const pdfBlob = new Blob([byteArray], { type: "application/pdf" });
          const blobUrl = URL.createObjectURL(pdfBlob);
          if (popup && !popup.closed) {
            popup.location.href = blobUrl;
          } else {
            window.open(blobUrl, "_blank");
          }
          return;
        } catch (base64Err) {
          console.warn("Error decoding embedded base64 content:", base64Err);
        }
      }

      // 2. Check if docParam has a direct HTTP/HTTPS URL
      const directUrl =
        typeof docParam === "string" && (docParam.startsWith("http://") || docParam.startsWith("https://"))
          ? docParam
          : typeof docParam === "object"
            ? docParam.attachmentUrl ||
            docParam.url ||
            docParam.serverURL ||
            docParam.fileUrl ||
            docParam.s3Url ||
            docParam.downloadUrl ||
            docParam.signedUrl
            : null;

      if (directUrl && typeof directUrl === "string" && (directUrl.startsWith("http://") || directUrl.startsWith("https://"))) {
        if (popup && !popup.closed) {
          popup.location.href = directUrl;
        } else {
          window.open(directUrl, "_blank");
        }
        return;
      }

      // 3. Extract filename / S3 key
      const rawFileName =
        typeof docParam === "string"
          ? docParam
          : docParam?.fileName ||
          docParam?.FileName ||
          docParam?.serverKey ||
          docParam?.key ||
          docParam?.awsKey ||
          docParam?.UUID ||
          docParam?.docID ||
          "";

      const fileName = String(rawFileName).trim();
      if (!fileName) {
        if (popup && !popup.closed) popup.close();
        showToast("Warning", "Document file name is missing", "warning");
        return;
      }

      if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
        if (popup && !popup.closed) {
          popup.location.href = fileName;
        } else {
          window.open(fileName, "_blank");
        }
        return;
      }

      const fileBasename = fileName.split("/").pop() || fileName;
      const typeStr = (nocType as string) || "";
      const modulePrefix =
        typeStr === "NOC_EXINDIA"
          ? "NOC_EXINDIA"
          : typeStr === "NOC_PASSPORT"
            ? "NOC_PASSPORT"
            : typeStr === "NOC_HIGHERSTUDY"
              ? "NOC_HIGHER_EDU"
              : typeStr.includes("ONLINECOURSE")
                ? "NOC_ONLINECOURSE"
                : "NOC_OUTJOB";

      const candidateKeys = Array.from(
        new Set([
          fileName,
          `noc_doc/${fileBasename}`,
          fileBasename,
          `${modulePrefix}/${fileBasename}`,
          `NOC_HIGHER_EDU/${fileBasename}`,
          `NOC_OUTJOB/${fileBasename}`,
          `NOC_EXINDIA/${fileBasename}`,
          `NOC_PASSPORT/${fileBasename}`,
          `NOC_ONLINECOURSE/${fileBasename}`,
        ])
      ).filter(Boolean);

      // 4. Request Presigned URL from S3 bucket endpoint
      for (const key of candidateKeys) {
        try {
          const endPoint = `${BASE_URLS.BASE_URL}aws-bucket/get-presigned-url`;
          const res: any = await Api.post(endPoint, { key });
          const resData = res?.data;
          let pUrl = "";
          if (typeof resData === "string") {
            pUrl = resData;
          } else if (typeof resData?.data === "string") {
            pUrl = resData.data;
          } else if (typeof resData?.data?.url === "string") {
            pUrl = resData.data.url;
          } else if (typeof resData?.url === "string") {
            pUrl = resData.url;
          } else if (typeof resData?.presignedUrl === "string") {
            pUrl = resData.presignedUrl;
          }

          if (pUrl && (pUrl.startsWith("http://") || pUrl.startsWith("https://"))) {
            if (popup && !popup.closed) {
              popup.location.href = pUrl;
            } else {
              window.open(pUrl, "_blank");
            }
            return;
          }
        } catch (keyErr) {
          console.warn(`Failed trying S3 key "${key}":`, keyErr);
        }
      }

      // 5. Fallback: try docID endpoint if presigned URL is unavailable
      const docID = typeof docParam === "object" ? (docParam.docID || docParam.docId || docParam.id) : null;
      if (docID) {
        try {
          const docRes: any = await Api.post(BASE_URLS.BASE_URL + "noc/noc-outjob-doc-by-docid", { docId: docID });
          const docData = docRes?.data?.d?.results?.[0] || docRes?.data?.data?.d?.results?.[0] || docRes?.data?.data;
          const base64Content = docData?.Content || docData?.FileContent || docData?.content;
          if (base64Content) {
            const cleanBase64 = base64Content.replace(/^data:.*?,/, "");
            const byteCharacters = atob(cleanBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const pdfBlob = new Blob([byteArray], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(pdfBlob);
            if (popup && !popup.closed) {
              popup.location.href = blobUrl;
            } else {
              window.open(blobUrl, "_blank");
            }
            return;
          }
        } catch (apiErr) {
          console.error("Error fetching doc by docID:", apiErr);
        }
      }

      if (popup && !popup.closed) {
        popup.close();
      }
      showToast("Error", "Unable to open file. Please try again.", "danger");
    } catch (error) {
      if (popup && !popup.closed) {
        popup.close();
      }
      console.error("Error opening document:", error);
      showToast("Error", "Unable to open file. Please try again.", "danger");
    }
  };

  const handleHrFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
      showToast("Error", "File type not allowed", "danger");
      return;
    }
    if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
      showToast("Error", "File size exceeds 2MB limit", "danger");
      return;
    }

    try {
      setHrDocUploading(true);
      const reader = new FileReader();
      reader.onload = async () => {
        const fullBase64 = reader.result as string;
        const base64String = fullBase64.includes(",") ? fullBase64.split(",")[1] : fullBase64;
        const moduleName = nocType === "NOC_HIGHERSTUDY" ? "NOC_HIGHER_EDU" : "NOC_OUTJOB";
        const res = await Api.post(`${BASE_URLS.BASE_URL}aws-bucket/upload-file`, {
          filestring: base64String,
          mime_type: file.type,
          module: moduleName,
        });
        setHrDocUploading(false);
        if (res?.data?.data) {
          setHrUploadedDocs((prev) => [
            ...prev,
            {
              fileName: file.name,
              fileSize: `${Math.round(file.size / 1024)}KB`,
              serverKey: res.data.data.key,
              serverURL: res.data.data.url,
              typeOfAttachedDoc: hrDocType,
            },
          ]);
          showToast("Success", "Document uploaded successfully", "success");
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setHrDocUploading(false);
      showToast("Error", "Failed to upload document", "danger");
    }
  };

  const uploadOutjobDocuments = async () => {
    if (hrUploadedDocs.length === 0) {
      showToast("Warning", "Please upload at least one document", "warning");
      return;
    }
    try {
      const payload = {
        Action: "U",
        ReferenceNumber: nocData?.referenceNumber,
        documents: hrUploadedDocs.map((doc) => ({
          fileName: doc.serverKey,
          typeOfAttachedDoc: doc.typeOfAttachedDoc || "O",
        })),
      };
      const endpoint = nocType === "NOC_HIGHERSTUDY"
        ? `${BASE_URLS.BASE_URL}noc/noc-higherstudies-approval-documents`
        : `${BASE_URLS.BASE_URL}noc/noc-outjob-approval-documents`;
      const res = await Api.post(endpoint, payload);
      if (res?.data) {
        showToast("Success", "Documents submitted successfully", "success");
        setHrUploadedDocs([]);
        handleRefresh();
      }
    } catch (err: any) {
      showToast("Error", err?.message || "Failed to submit documents", "danger");
    }
  };

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    type: "success" as "success" | "danger" | "warning" | "primary",
  });

  // Chat state (simplified - most data from Redux now)
  const [newMessage, setNewMessage] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  // Helper to determine if a message is sent by current logged in user
  const isMyMessage = (msg: ChatMessage): boolean => {
    const userStr = localStorage.getItem("user");
    const userObj = userStr && userStr !== "undefined" ? JSON.parse(userStr) : {};
    const myEmpId = String(userObj?.empId || user?.empId || "").trim().replace(/^0+/, "");
    const senderEmpId = String(msg.senderEmpId || "").trim().replace(/^0+/, "");
    if (myEmpId && senderEmpId) {
      return myEmpId === senderEmpId;
    }
    const isApprover =
      userObj?.role === "APPROVER" ||
      userObj?.user_type === "APPROVER" ||
      Boolean(userObj?.approver_level);
    if (isApprover) {
      return msg.senderType === "approver" || msg.senderType === "admin";
    }
    return msg.senderType === "employee";
  };

  // Auto-scroll to bottom of chat when new messages arrive or when chat is opened
  useEffect(() => {
    if (showChat) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages.length, showChat]);

  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    title: string;
    isImage: boolean;
  } | null>(null);

  const handleAttachmentClick = async (rawAttachment: any) => {
    if (!rawAttachment) return;
    const opened = await openChatAttachment(rawAttachment, nocData?.id, nocType, dispatch, attachments);
    if (!opened) {
      showToast(
        "Warning",
        "Unable to open attachment. File link not found.",
        "warning"
      );
    }
  };

  // Document request states
  const [showDocumentTypeDialog, setShowDocumentTypeDialog] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("");
  const [pendingMessage, setPendingMessage] = useState<string>("");

  // Function to detect if message is requesting a document
  const isDocumentRequest = (message: string): boolean => {
    const documentKeywords = [
      "document",
      "documents",
      "file",
      "files",
      "upload",
      "attach",
      "attachment",
      "send",
      "provide",
      "submit",
      "certificate",
      "proof",
      "evidence",
      "scan",
      "copy",
      "copies",
    ];

    const lowerMessage = message.toLowerCase();
    return documentKeywords.some((keyword) => lowerMessage.includes(keyword));
  };

  // Auto-refresh messages every 5 seconds when chat is open
  useEffect(() => {
    if (
      showChat &&
      nocData?.id
    ) {
      dispatch(fetchMessages({ nocRequestId: nocData.id }));

      const interval = setInterval(() => {
        dispatch(fetchMessages({ nocRequestId: nocData.id }));
      }, 5000);

      setRefreshInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [showChat, nocData?.id, dispatch, nocType]);

  // Fetch unread count on component mount
  useEffect(() => {
    if (
      nocData?.id
    ) {
      dispatch(fetchUnreadCount(nocData.id));
      dispatch(fetchMessages({ nocRequestId: nocData.id }));
    }
  }, [nocData?.id, dispatch, nocType]);

  // Helper function to show toast
  const showToast = (
    title: string,
    message: string,
    type: "success" | "danger" | "warning" | "primary"
  ) => {
    // Format message to be user-friendly (Bug 47)
    const formattedMessage = formatErrorMessage(message);

    setToast({
      show: true,
      title,
      message: formattedMessage,
      type,
    });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  const formatErrorMessage = (message: string): string => {
    if (!message || typeof message !== "string") return message;

    // If it's a long message or doesn't look like a key, return as is
    if (message.includes(" ") && !message.includes("_")) return message;

    return message
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
      .replace(/D&ar/i, "D&AR")
      .replace(/Noc/i, "NOC")
      .replace(/Id/i, "ID");
  };
  // Format date helper
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString || dateString === "Invalid date") return "N/A";
    // Use UTC to prevent 1-day shifts due to timezone (Bug 45)
    return moment.utc(dateString).format("DD-MM-YYYY");
  };

  const getDnarStatusLabel = (status: string | undefined): string => {
    if (!status) return "N/A";
    const upper = String(status).toUpperCase();
    const mapping: Record<string, string> = {
      N: "No D&AR contemplated against the employee",
      P: "D&AR Pending/ Deemed Suspension /Suspension/ Court Case Pending",
      M: "D&AR completed; Minor penalty has been imposed",
      J: "D&AR completed; Major penalty has been imposed",
    };
    return mapping[upper] || status;
  };

  const calculateAge = (dateOfBirth?: string | null | undefined) => {
    if (!dateOfBirth || dateOfBirth === "N/A" || dateOfBirth === "-") return "";

    // Handle both DD-MM-YYYY format and ISO date format
    let birthDate;
    if (dateOfBirth.includes("-") && dateOfBirth.split("-").length === 3) {
      // DD-MM-YYYY format
      const parts = dateOfBirth.split("-");
      if (parts[0].length === 2) {
        // DD-MM-YYYY format
        birthDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`); // Convert to YYYY-MM-DD
      } else {
        // YYYY-MM-DD format
        birthDate = new Date(dateOfBirth);
      }
    } else if (dateOfBirth.includes(".")) {
      // DD.MM.YYYY format (legacy support)
      birthDate = new Date(dateOfBirth.split(".").reverse().join("-")); // Convert to YYYY-MM-DD
    } else {
      // ISO date format
      birthDate = new Date(dateOfBirth);
    }

    const today = new Date();

    // Calculate years
    let years = today.getFullYear() - birthDate.getFullYear();

    // Calculate months
    let months = today.getMonth() - birthDate.getMonth();

    // Calculate days
    let days = today.getDate() - birthDate.getDate();

    // Adjust for negative days
    if (days < 0) {
      months--;
      const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += lastMonth.getDate();
    }

    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    return `${years}y, ${months}m, ${days}d`;
  };

  // Format boolean values
  const formatBoolean = (
    value: string | boolean | null | undefined
  ): string => {
    if (value === "Y" || value === true) return "Yes";
    if (value === "N" || value === false) return "No";
    if (value === null || value === undefined || value === "") return "N/A";
    return value?.toString() || "N/A";
  };

  // Check if field is editable based on enabled_fields_fields_json
  const isFieldEditable = (fieldName: string): boolean => {
    return getFieldStatus(fieldName) === "EDITABLE";
  };

  const getFieldStatus = (fieldName: string): "EDITABLE" | "VIEW_ONLY" | "HIDDEN" => {
    const fieldsJson = nocData?.enabled_fields_fields_json || nocData?.enabled_fields_json;
    if (!fieldsJson) return "VIEW_ONLY";

    const aliases: Record<string, string[]> = {
      DandARStatus: ["DandARStatus", "DnARStatus", "dnARStatus", "dandARStatus"],
      DnARStatus: ["DnARStatus", "DandARStatus", "dnARStatus", "dandARStatus"],
      DandARPenaltyEffective: [
        "DandARPenaltyEffective",
        "DnARPenaltyEffecti",
        "DnARPenaltyEffective",
        "dnARPenaltyEffective",
        "dnARPenaltyEffecti",
      ],
      DnARPenaltyEffecti: [
        "DnARPenaltyEffecti",
        "DandARPenaltyEffective",
        "DnARPenaltyEffective",
        "dnARPenaltyEffective",
        "dnARPenaltyEffecti",
      ],
      Remarks: ["Remarks", "remarks", "Remarks1", "remarks1", "HRemarks", "hRemarks"],
      HRemarks: ["HRemarks", "hRemarks", "Remarks1", "remarks1", "Remarks", "remarks"],
      Department: ["Department", "department", "Dept", "dept"],
      PlaceOfVisitNOC: ["PlaceOfVisitNOC", "placeOfVisitNoc", "PlaceOfVisit", "placeOfVisit"],
      PurposeOfVisit: ["PurposeOfVisit", "purposeOfVisit"],
      PeriodOfVisitFrom: ["PeriodOfVisitFrom", "periodOfVisitFrom", "PeriodFrom", "periodFrom"],
      PeriodOfVisitTo: ["PeriodOfVisitTo", "periodOfVisitTo", "PeriodTo", "periodTo"],
      LeaveDetails: ["LeaveDetails", "leaveDetails"],
      FinalDandARVigilance: ["FinalDandARVigilance", "finalDandARVigilance"],
      ReasonForRejection: ["ReasonForRejection", "reasonForRejection"],
    };

    const keysToTry = aliases[fieldName] || [fieldName];
    for (const key of keysToTry) {
      if (fieldsJson[key] !== undefined) {
        const val = fieldsJson[key];
        if (val === "X" || val === "V") return "EDITABLE";
        if (val === "" || val === null) return "VIEW_ONLY";
        return "VIEW_ONLY";
      }
    }

    // Try case-insensitive / substring matching if not found directly
    const lower = fieldName.toLowerCase();
    for (const k of Object.keys(fieldsJson)) {
      const kLower = k.toLowerCase();
      if (
        kLower === lower ||
        (lower.includes("dnar") && kLower.includes("dnar")) ||
        (lower.includes("dandar") && (kLower.includes("dnar") || kLower.includes("dandar"))) ||
        (lower.includes("penalty") && kLower.includes("penalty"))
      ) {
        const val = fieldsJson[k];
        if (val === "X" || val === "V") return "EDITABLE";
        if (val === "" || val === null) return "VIEW_ONLY";
        return "VIEW_ONLY";
      }
    }

    return "HIDDEN";
  };

  // Handle field value change
  const handleFieldChange = (fieldName: string, value: any) => {
    setEditableFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Get field value (from editable state or original data)
  const getFieldValue = (fieldName: string, originalValue: any) => {
    return editableFields[fieldName] !== undefined
      ? editableFields[fieldName]
      : originalValue;
  };

  // Save changes
  const handleSave = () => {
    console.log("Saving changes:", editableFields);
    setIsEditing(false);
    showToast("Success", "Changes saved successfully", "success");
  };

  // Cancel editing
  const handleCancel = () => {
    setEditableFields({});
    setIsEditing(false);
  };

  // API action state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<"A" | "R" | null>(null);

  // Handle VPF Approve/Reject API call
  const handleVpfApproveReject = async (action: "A" | "R") => {
    if (!vpfItem?.requestID) {
      showToast("Error", "VPF Request ID not found", "danger");
      return;
    }

    if (action === "R") {
      const userRemarks = (hRemarks || remarks || "").trim();
      if (!userRemarks) {
        showToast("Error", "Approver remarks/comments are mandatory for rejection.", "danger");
        return;
      }
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const payload: any = {
        Action: action,
        ReferenceNumber: vpfItem.requestID,
      };

      console.log("Sending VPF API request:", payload);

      const response = await Api.post(
        `${BASE_URLS.BASE_URL}vpf-request/approve_reject_action`,
        payload
      );

      if (response.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        showToast(
          "Success",
          `VPF application ${actionText} successfully`,
          "success"
        );

        // Optionally refresh the data or navigate back
        setTimeout(() => {
          handleBack();
        }, 2000);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("VPF API Error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to process request";
      showToast("Error", errorMessage, "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  // Handle Approve/Reject API calls (NOCs)
  const handleApproveReject = async (action: "A" | "R") => {
    if (!nocData?.referenceNumber) {
      showToast("Error", "Reference number not found", "danger");
      return;
    }

    const isDnarEditable =
      nocData?.enabled_fields_fields_json?.DandARStatus === "X" ||
      nocData?.enabled_fields_json?.DandARStatus === "X" ||
      nocData?.enabled_fields_fields_json?.dandARStatus === "X" ||
      nocData?.enabled_fields_json?.dandARStatus === "X";

    if (action === "A" && isDnarEditable) {
      if (!dnARStatus) {
        showToast("Error", "Please select D&AR status of the employee.", "danger");
        return;
      }
      if ((dnARStatus === "M" || dnARStatus === "J") && !dnARPenaltyEffective) {
        showToast("Error", "Please select D&AR Penalty Effective Date.", "danger");
        return;
      }
    }

    const isRejectionReasonEnabled =
      nocData?.enabled_fields_fields_json?.ReasonForRejection === "X" ||
      nocData?.enabled_fields_json?.ReasonForRejection === "X";

    if (action === "R" && nocType === "NOC_OUTJOB" && isRejectionReasonEnabled) {
      if (!selectedRejectionReason) {
        showToast("Error", "Please select a reason for rejection.", "danger");
        return;
      }
    }

    if (action === "R") {
      const userRemarks = (hRemarks || remarks || "").trim();
      if (!userRemarks) {
        showToast("Error", "Approver remarks/comments are mandatory for rejection.", "danger");
        return;
      }
    }

    if (nocType === "NOC_ONLINECOURSE-REIMB" && action === "A") {
      const isCL =
        nocData?.claimOrOnlyAdditi === "CL" ||
        nocData?.reimbursement_type?.type_id === "CL" ||
        nocData?.reimbursement_type?.type_title?.toLowerCase()?.includes("reimburse");

      if (isCL) {
        const approved = parseFloat(approvedAmount || "0");
        const requested = parseFloat(nocData?.requestAmount || nocData?.requestedAmount || "0");
        if (isNaN(approved) || approved <= 0) {
          showToast("Error", "Approved amount must be greater than 0.", "danger");
          return;
        }
        if (requested > 0 && approved > requested) {
          showToast("Error", "Approved amount cannot exceed requested amount.", "danger");
          return;
        }
      }

      const isFirstApprover = parseInt(nocData?.approverLevel || nocData?.ApproverLevel || "1") < 2;
      if (isFirstApprover && !(hRemarks || remarks || "").trim()) {
        showToast("Error", "Remark is required for approval.", "danger");
        return;
      }
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      let payload: any = {};

      if (nocType === "NOC_OUTJOB") {
        payload = {
          Approve_Reject_flag: action,
          ReferenceNumber: nocData.referenceNumber,
          remarks: remarks || nocData?.Remarks || nocData?.remarks || "",
          remarks1: hRemarks || nocData?.Remarks1 || nocData?.remarks1 || "",
          DandARStatus: dnARStatus || nocData?.DandARStatus || nocData?.dandARStatus || "",
          dandARPenaltyEffective: dnARPenaltyEffective || nocData?.dandARPenaltyEffective || nocData?.DandARPenaltyEffective || "",
          FinalDandARVigilance: finalDandARVigilance || nocData?.finalDandARVigilance || nocData?.FinalDandARVigilance || "",
          PostAppliedFor: getFieldValue("postAppliedFor", nocData?.PostAppliedFor || nocData?.postAppliedfor || ""),
          OrganisationInWhich: getFieldValue("organisationInWhich", nocData?.OrganisationInWhich || nocData?.organisationInWhich || ""),
          NatureOfOutside: getFieldValue("natureOfOutside", nocData?.natureDetails?.natureId || nocData?.natureOfOutside || ""),
          AdvertisementNo: getFieldValue("advertisementNo", nocData?.advertisementNo || ""),
          AdvertisementDate: getFieldValue("advertisementDate", nocData?.advertisementDate || ""),
          ReasonForRejection: action === "R" ? (selectedRejectionReason || "") : "",
        };
      } else if (nocType == "NOC_HIGHERSTUDY") {
        payload = {
          Approve_Reject_flag: action,
          ReferenceNumber: nocData.referenceNumber,
          DnARStatus: dnARStatus || undefined,
          dandARStatus: dnARStatus || undefined,
          DnARPenaltyEffecti: dnARPenaltyEffective || undefined,
          dandARPenaltyEffective: dnARPenaltyEffective || undefined,
          remarks: "",
          hRemarks: hRemarks || remarks || "",
          courseName: getFieldValue("courseName", nocData.courseName || ""),
          institute: getFieldValue("institute", nocData.institute || ""),
          partTimeOrCorresp: getFieldValue("partTimeOrCorresp", nocData.partTimeOrCorresp || nocData.modeOfCourse || ""),
        };
      } else if (nocType == "NOC_ONLINECOURSE") {
        payload = {
          Approve_Reject_flag: action,
          ReferenceNumber: nocData.referenceNumber,
          remarks: "",
          hRemarks: hRemarks || "",
        };
      } else if (nocType === "NOC_PASSPORT") {
        const currentDnar = dnARStatus || nocData?.dnARStatus || "";
        payload = {
          Approve_Reject_flag: action,
          ReferenceNumber: nocData.referenceNumber,
          HRemarks: hRemarks || "",
          Remarks: remarks || nocData?.hRemarks || "",
          DnARStatus: currentDnar,
          DnARPenaltyEffecti:
            (currentDnar === "M" || currentDnar === "J")
              ? (dnARPenaltyEffective || nocData?.dnARPenaltyEffecti || "")
              : "",
        };
      } else if (nocType == "NOC_ONLINECOURSE-REIMB") {
        payload = {
          Approve_Reject_flag: action,
          referenceNumber: nocData?.referenceNumber,
          hRemarks: hRemarks || "",
          EmpID: nocData?.empID,
          approverUserID: nocData?.approver?.empId,
          approvedAmount: approvedAmount || "",
        };
      } else if (nocType === "NOC_EXINDIA") {
        payload = {
          Approve_Reject_flag: action,
          ReferenceNumber: nocData.referenceNumber,
          HRemarks: hRemarks || nocData?.Remarks1 || "",
          Remarks: remarks || nocData?.Remarks || "",
          DnARStatus: dnARStatus || nocData?.DandARStatus || nocData?.dnARStatus || "",
          DnARPenaltyEffecti: dnARPenaltyEffective || nocData?.dnARPenaltyEffecti || nocData?.DnARPenaltyEffecti || "",
          placeOfVisitNoc: getFieldValue("PlaceOfVisitNOC", nocData?.placeOfVisit || nocData?.placeOfVisitNoc || ""),
          purposeOfVisit: getFieldValue("PurposeOfVisit", nocData?.purposeOfVisit || ""),
          periodOfVisitFrom: getFieldValue("PeriodOfVisitFrom", nocData?.periodFrom || nocData?.periodOfVisitFrom || ""),
          periodOfVisitTo: getFieldValue("PeriodOfVisitTo", nocData?.periodTo || nocData?.periodOfVisitTo || ""),
          leaveDetails: getFieldValue("LeaveDetails", nocData?.leaveDetails || ""),
          department: getFieldValue(
            "Department",
            nocData?.department ||
            nocData?.dept ||
            nocData?.departmentName ||
            nocData?.employee?.details?.sap_department_designation_wings?.department_name ||
            nocData?.employee?.sap_department_designation_wings?.department_name ||
            nocData?.employee?.details?.department_name ||
            nocData?.employee?.department_details?.departmentName ||
            nocData?.employee?.department ||
            ""
          ),
        };
      } else {
        payload = {
          Approve_Reject_flag: action,
          ReferenceNumber: nocData.referenceNumber,
          DnARStatus: dnARStatus || undefined,
          dandARStatus: dnARStatus || undefined,
          DnARPenaltyEffecti: dnARPenaltyEffective || undefined,
          dandARPenaltyEffective: dnARPenaltyEffective || undefined,
          Remarks: remarks || "",
          HRemarks: hRemarks || "",
        };
      }

      console.log("Sending API request:", payload);

      let apiEndPoint = "";
      if (nocType === "NOC_OUTJOB") {
        apiEndPoint = "sap-master-worklist/approve_reject_action";
      } else if (nocType === "NOC_EXINDIA") {
        apiEndPoint = "noc-ex-india/approve_reject_action";
      } else if (nocType === "NOC_HIGHERSTUDY") {
        apiEndPoint = "noc-higherstudies/approve_reject_action";
      } else if (nocType === "NOC_ONLINECOURSE") {
        apiEndPoint = "noc-onlinecourses/approve_reject_action";
      } else if (nocType === "NOC_PASSPORT") {
        apiEndPoint = "noc-visa-passport/approve_reject_action";
      } else if (nocType === "NOC_ONLINECOURSE-REIMB") {
        apiEndPoint = "noc-reimbursement/approve_reject_action";
      }

      const response = await Api.post(
        `${BASE_URLS.BASE_URL}${apiEndPoint}`,
        payload
      );

      if (response.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        showToast(
          "Success",
          `NOC application ${actionText} successfully`,
          "success"
        );

        // Clear fields after successful action
        setDnARStatus("");
        setDnARPenaltyEffective("");
        setRemarks("");
        setHRemarks("");

        // Optionally refresh the data or navigate back
        setTimeout(() => {
          handleBack();
        }, 2000);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("API Error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to process request";
      showToast("Error", errorMessage, "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };
  // Chat functions
  const handleSendMessage = async () => {
    const trimmedMsg = newMessage.trim();

    // Condition matching mobile app: if attachment is selected, message text is required
    if (selectedFiles.length > 0 && !trimmedMsg) {
      showToast("Warning", "Please type a message with your attachment before sending.", "warning");
      return;
    }

    if (!trimmedMsg && selectedFiles.length === 0) return;
    if (!nocData?.id) return;

    // Check if message contains document request keywords
    if (isDocumentRequest(trimmedMsg) && !selectedDocumentType) {
      setPendingMessage(trimmedMsg);
      setShowDocumentTypeDialog(true);
      return;
    }

    try {
      let uploadedDocs: any[] = [];
      if (selectedFiles.length > 0) {
        const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
        if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
          uploadedDocs = prepareUploadedAttachments(
            uploadResult.payload,
            selectedFiles,
            selectedDocumentType
          );
        } else {
          showToast("Error", "Failed to upload attached files. Please try again.", "danger");
          return;
        }
      }

      const attNames = uploadedDocs.map((d: any) => d.fileName).join(", ");
      const finalMessage = uploadedDocs.length > 0
        ? (trimmedMsg ? `${trimmedMsg}\n📎 ${attNames}` : `📎 ${attNames}`)
        : trimmedMsg;

      await dispatch(
        sendChatMessage({
          nocRequestId: nocData.id,
          message: finalMessage,
          attachments: uploadedDocs,
          documents: uploadedDocs,
          documentType: selectedDocumentType || undefined,
        })
      );

      setNewMessage("");
      setSelectedFiles([]);
      setSelectedDocumentType("");
      setPendingMessage("");

      // Refresh messages after sending
      dispatch(fetchMessages({ nocRequestId: nocData.id }));
      if (uploadedDocs.length > 0) {
        dispatch(fetchChatAttachments({ nocRequestId: nocData.id, nocType: "OUTSIDE-JOB" }));
      }

      showToast("Success", "Message sent successfully", "success");
    } catch (error) {
      showToast("Error", "Failed to send message", "danger");
    }
  };

  const handleDocumentTypeSelection = async () => {
    if (!selectedDocumentType) {
      showToast("Warning", "Please select a document type", "warning");
      return;
    }

    setShowDocumentTypeDialog(false);

    // Now send the message with the selected document type
    if (pendingMessage.trim() && nocData?.id) {
      try {
        let uploadedDocs: any[] = [];
        if (selectedFiles.length > 0) {
          const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
          if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
            uploadedDocs = prepareUploadedAttachments(
              uploadResult.payload,
              selectedFiles,
              selectedDocumentType
            );
          }
        }

        await dispatch(
          sendChatMessage({
            nocRequestId: nocData.id,
            message: pendingMessage,
            attachments: uploadedDocs,
            documentType: selectedDocumentType,
          })
        );

        setNewMessage("");
        setSelectedFiles([]);
        setSelectedDocumentType("");
        setPendingMessage("");

        // Refresh messages after sending
        dispatch(fetchMessages({ nocRequestId: nocData.id }));

        showToast("Success", "Document request sent successfully", "success");
      } catch (error) {
        showToast("Error", "Failed to send message", "danger");
      }
    }
  };

  const handleCancelDocumentType = () => {
    setShowDocumentTypeDialog(false);
    setSelectedDocumentType("");
    setPendingMessage("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleChatToggle = () => {
    setShowChat(!showChat);
    if (!showChat && nocData?.id) {
      dispatch(fetchMessages({ nocRequestId: nocData.id }));
      dispatch(fetchChatAttachments({ nocRequestId: nocData.id, nocType }));
    } else {
      dispatch(clearMessages());
      // Clear file states when closing chat
      setSelectedFiles([]);
      setUploadingFiles(new Set());
    }
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        // Validate file type
        if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
          errors.push(
            `${file.name
            }: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(
              ", "
            )}`
          );
          return;
        }

        // Validate file size
        if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
          errors.push(
            `${file.name}: File size too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`
          );
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        showToast(
          "Error",
          "File validation errors:\n" + errors.join("\n"),
          "danger"
        );
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }
    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const confirmFileUpload = async (index: number) => {
    const file = selectedFiles[index];
    if (!file || !nocData?.id) return;

    try {
      setUploadingFiles((prev) => new Set(prev).add(index));

      // Upload file to AWS
      const uploadResult = await dispatch(uploadChatFiles([file]));

      if (uploadChatFiles.fulfilled.match(uploadResult)) {
        const uploadData = uploadResult.payload[0];
        const uploadedDocs = prepareUploadedAttachments([uploadData], [file], selectedDocumentType);

        // Immediately send as message with attachment
        await dispatch(
          sendChatMessage({
            nocRequestId: nocData.id,
            message: `📎 ${file.name}`,
            attachments: uploadedDocs,
            documentType: selectedDocumentType || undefined,
          })
        );

        // Remove from selected files
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

        // Refresh messages to show the new attachment message
        dispatch(fetchMessages({ nocRequestId: nocData.id }));

        showToast("Success", "File uploaded successfully", "success");
      } else {
        console.error("Upload failed:", uploadResult.payload);
        showToast(
          "Error",
          "Failed to upload file. Please try again.",
          "danger"
        );
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      showToast("Error", "Failed to upload file. Please try again.", "danger");
    } finally {
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const formatMessageTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Fetch NOC data on component mount
  useEffect(() => {
    if (requestID) {
      setIsInitialLoading(true);
      dispatch(
        fetchNocData({
          requestID,
          type: nocType,
        })
      ).finally(() => {
        setIsInitialLoading(false);
      });
    } else {
      setIsInitialLoading(false);
      showToast("Error", "Request ID is required", "danger");
    }

    return () => {
      dispatch(clearNocData());
      dispatch(clearNocError());
    };
  }, [requestID, nocType, dispatch, isVpf]);

  useEffect(() => {
    if (!isVpf) {
      dispatch(fetchNocDnARDropdownData());
    }
  }, [dispatch, isVpf]);

  // Handle navigation back
  const handleBack = () => {
    navigate(-1);
  };

  // Handle refresh
  const handleRefresh = () => {
    if (requestID) {
      setIsInitialLoading(true);
      dispatch(
        fetchNocData({
          requestID,
          type: nocType,
        })
      ).finally(() => {
        setIsInitialLoading(false);
      });
    }
  };

  // Helper function to convert numbers to ordinal format
  const getOrdinalNumber = (num: string | number | undefined) => {
    if (!num) return "-";
    const n = typeof num === "string" ? parseInt(num) : num;
    if (isNaN(n)) return num;

    const suffixes = ["th", "st", "nd", "rd"];
    const remainder = n % 100;

    if (remainder >= 11 && remainder <= 13) {
      return n + "th";
    }

    const lastDigit = n % 10;
    const suffix = suffixes[lastDigit] || suffixes[0];
    return n + suffix;
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };
  if (nocLoading || isInitialLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center min-vh-100">
            <div className="text-center">
              <div
                className="spinner-border text-primary mb-3"
                style={{ width: "3rem", height: "3rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="text-muted">Loading NOC Details...</h5>
              <p className="text-muted">
                Please wait while we fetch the information.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (nocError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card border-danger">
                <div className="card-header bg-danger-subtle">
                  <h5 className="card-title text-danger mb-0">
                    <i className="ti ti-alert-circle me-2"></i>
                    Error Loading NOC Details
                  </h5>
                </div>
                <div className="card-body">
                  <p className="text-danger mb-3">{nocError}</p>
                  <div className="d-flex gap-2">
                    <button onClick={handleRefresh} className="btn btn-danger">
                      <i className="ti ti-refresh me-2"></i>
                      Retry
                    </button>
                    <button
                      onClick={handleBack}
                      className="btn btn-outline-secondary"
                    >
                      <i className="ti ti-arrow-left me-2"></i>
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isVpf && !nocData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="ti ti-file-x display-4 text-muted"></i>
                  <h5 className="mt-3 text-muted">No NOC Data Available</h5>
                  <p className="text-muted">
                    No NOC data found for the requested ID.
                  </p>
                  <button onClick={handleBack} className="btn btn-primary">
                    <i className="ti ti-arrow-left me-2"></i>
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isVpf) {
    if (!vpfItem) {
      return (
        <div className="page-wrapper">
          <div className="content">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-body text-center py-5">
                    <i className="ti ti-file-x display-4 text-muted"></i>
                    <h5 className="mt-3 text-muted">No VPF Data Available</h5>
                    <p className="text-muted">
                      No VPF data found for the requested ID.
                    </p>
                    <button onClick={handleBack} className="btn btn-primary">
                      <i className="ti ti-arrow-left me-2"></i>
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <div className="d-flex align-items-center gap-2">
                <i
                  className="ti ti-arrow-left"
                  onClick={handleBack}
                  style={{ cursor: "pointer", fontSize: "1.5rem" }}
                ></i>
                <h3 className="page-title mb-0">VPF Request Approval</h3>
              </div>
              <nav className="mt-2">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <a href="#" onClick={handleBack}>Tasks</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">VPF Details</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex gap-2 align-items-center">
              {nocData?.RequestStatus === 'T' && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => handleVpfApproveReject("A")}
                    disabled={isProcessing}
                  >
                    {isProcessing && processingAction === "A" ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check me-2"></i>Approve
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleVpfApproveReject("R")}
                    disabled={isProcessing}
                  >
                    {isProcessing && processingAction === "R" ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-x me-2"></i>Reject
                      </>
                    )}
                  </button>
                </>
              )}
              <button onClick={handleBack} className="btn btn-outline-secondary">
                <i className="ti ti-arrow-left me-2"></i>Go Back
              </button>
            </div>
          </div>

          {/* Status Banner */}
          <div className={`alert ${nocData?.RequestStatus?.toLowerCase().includes("approve") ? "alert-success" : "alert-warning"} d-flex align-items-center mb-4`}>
            <i className="ti ti-clock me-3 text-warning" style={{ fontSize: "1.75rem" }}></i>
            <div>
              <div className="text-muted small">Request Status</div>
              <div className="fw-bold">{nocData?.RequestStatus === 'T' ? 'To Be Approved' : nocData?.RequestStatus || vpfItem.status || "Pending"}</div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0 d-flex align-items-center">
                <i className="ti ti-plane text-danger me-2" style={{ fontSize: "1.5rem" }}></i>Application Info
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Employee ID</label>
                  <div className="text-dark fw-medium">{nocData?.EmpId || vpfItem.employee?.empId || vpfItem.empId || "-"}</div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Reference No</label>
                  <div className="text-dark fw-medium">{nocData?.ReferenceNumber || vpfItem.requestID || "-"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0 d-flex align-items-center">
                <i className="ti ti-users text-danger me-2" style={{ fontSize: "1.5rem" }}></i>Employee Details
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Name (Full, Not in abbreviation)</label>
                  <div className="text-dark fw-medium">
                    {nocData?.employee ? `${nocData.employee.firstname || ""} ${nocData.employee.lsastname || ""}`.trim() : vpfItem.employee?.fullName || "-"}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Employee No</label>
                  <div className="text-dark fw-medium">{nocData?.employee?.empId || vpfItem.employee?.empId || "-"}</div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Designation</label>
                  <div className="text-dark fw-medium">{nocData?.employee?.sap_department_designation_wings?.designation_name || "-"}</div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Date of Joining</label>
                  <div className="text-dark fw-medium">
                    {nocData?.employee?.date_of_joining ? new Date(nocData.employee.date_of_joining).toLocaleDateString('en-GB').replace(/\//g, '-') : "-"}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Employment Status</label>
                  <div className="text-dark fw-medium">
                    {nocData?.employee?.employmentType ||
                      nocData?.employee?.details?.employmentType ||
                      (nocData as any)?.employmentType ||
                      "-"}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Contact Details (Mobile No.)</label>
                  <div className="text-dark fw-medium">{nocData?.employee?.phone || "-"}</div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Email ID</label>
                  <div className="text-dark fw-medium">{nocData?.employee?.email || "-"}</div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Department</label>
                  <div className="text-dark fw-medium">{nocData?.employee?.sap_department_designation_wings?.department_name || "-"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0 d-flex align-items-center">
                <i className="ti ti-wallet text-danger me-2" style={{ fontSize: "1.5rem" }}></i>VPF Details
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Request Creation Date</label>
                  <div className="text-dark fw-medium">
                    {nocData?.created_at ? new Date(nocData.created_at).toLocaleDateString('en-GB').replace(/\//g, '-') : vpfItem.creationDate || "-"}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Amount</label>
                  <div className="text-dark fw-medium">
                    ₹{nocData?.VpfAmount ? parseFloat(nocData.VpfAmount).toFixed(3) : "0.000"}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Month</label>
                  <div className="text-dark fw-medium">
                    {nocData?.DeductionFromTheMonth || "-"}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted fw-semibold mb-1">Year</label>
                  <div className="text-dark fw-medium">{nocData?.DeductionFromTheYear || "-"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0 d-flex align-items-center">
                <i className="ti ti-user text-danger me-2" style={{ fontSize: "1.5rem" }}></i>Approver Details
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="text-muted fw-semibold mb-1">Approver Name</label>
                  <div className="text-dark fw-medium">
                    {nocData?.ApproverName || (nocData?.approver ? `${nocData.approver.firstname || ''} ${nocData.approver.lsastname || ''}`.trim() : "-")}
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="text-muted fw-semibold mb-1">Approver ID</label>
                  <div className="text-dark fw-medium">{nocData?.ApproverUserID || "-"}</div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  if (!nocData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="ti ti-file-x display-4 text-muted"></i>
                  <h5 className="mt-3 text-muted">No NOC Data Available</h5>
                  <p className="text-muted">
                    No NOC data found for the requested ID.
                  </p>
                  <button onClick={handleBack} className="btn btn-primary">
                    <i className="ti ti-arrow-left me-2"></i>
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center gap-2">
              <i
                className="ti ti-arrow-left"
                onClick={handleBack}
                style={{ cursor: "pointer", fontSize: "1.5rem" }}
              ></i>
              <h3 className="page-title mb-0">
                {nocType === "NOC_PASSPORT"
                  ? "Passport/Visa Request Details"
                  : nocType === "NOC_ONLINECOURSE-REIMB"
                    ? "Application for Reimbursement/Addition of Online Course"
                    : nocType === "NOC_ONLINECOURSE"
                      ? "Application for Online Course"
                      : `Application for ${nocType.replace("_", " ")}`}
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <a href="#" onClick={handleBack}>
                    Tasks
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {nocType === "NOC_PASSPORT"
                    ? "Passport/Visa Request Details"
                    : nocType === "NOC_ONLINECOURSE-REIMB"
                      ? "Application for Reimbursement/Addition of Online Course"
                      : "NOC Details"}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            {Boolean(nocType && ["NOC_HIGHERSTUDY", "NOC_OUTJOB", "NOC_EXINDIA"].includes(nocType)) &&
              (nocData?.requestStatus?.toUpperCase() === 'D') && (
                <button onClick={handleDownloadNocPDF} className="btn btn-success" disabled={isDownloadingNoc}>
                  <i className="ti ti-download me-2"></i>
                  {isDownloadingNoc ? "Downloading..." : "Download Digital NOC"}
                </button>
              )}
            <button onClick={handleRefresh} className="btn btn-outline-primary">
              <i className="ti ti-refresh me-2"></i>
              Refresh
            </button>
          </div>
        </div>


        {/* Top Request Status Banner */}
        <div className={`alert ${nocData?.requestStatus?.toUpperCase() === 'A' || nocData?.status?.toUpperCase() === 'APPROVED' ? 'alert-success' : nocData?.requestStatus?.toUpperCase() === 'R' || nocData?.status?.toUpperCase() === 'REJECTED' ? 'alert-danger' : 'alert-warning'} d-flex align-items-center justify-content-between mb-4 shadow-sm border`}>
          <div className="d-flex align-items-center gap-3">
            <div className="p-2 rounded-circle bg-white shadow-xs text-warning">
              <i className="ti ti-clock fs-24"></i>
            </div>
            <div>
              <div className="text-muted small fw-medium text-uppercase">Request Status</div>
              <div className="fw-bold fs-16 text-dark">{formatStatusText(nocData?.requestStatus || nocData?.status)}</div>
            </div>
          </div>
          <div>
            <span className={formatStatusBadgeClass(nocData?.requestStatus || nocData?.status)}>
              {formatStatusText(nocData?.requestStatus || nocData?.status)}
            </span>
          </div>
        </div>

        {/* Application Info Hero Card */}
        <div className="card mb-4">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0 d-flex align-items-center">
              <i className="ti ti-plane text-primary me-2"></i>
              Application Info
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className={nocType === "NOC_ONLINECOURSE-REIMB" || nocData?.referenceNumber1 || nocData?.online_course?.referenceNumber ? "col-md-4 col-sm-6" : "col-md-6 col-sm-6"}>
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-medium mb-1">Employee ID</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData?.empID || nocData?.empId || nocData?.employee?.empId || "-"}
                  </div>
                </div>
              </div>

              <div className={nocType === "NOC_ONLINECOURSE-REIMB" || nocData?.referenceNumber1 || nocData?.online_course?.referenceNumber ? "col-md-4 col-sm-6" : "col-md-6 col-sm-6"}>
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-medium mb-1">Reference No</small>
                  <div className="fw-bold text-dark fs-15 font-monospace">
                    {nocData?.referenceNumber || "-"}
                  </div>
                </div>
              </div>

              {(nocType === "NOC_ONLINECOURSE-REIMB" || nocData?.referenceNumber1 || nocData?.online_course?.referenceNumber) && (
                <div className="col-md-4 col-sm-6">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block text-uppercase fw-medium mb-1">Associated Reference No</small>
                    <div className="fw-bold text-dark fs-15 font-monospace">
                      {nocData?.referenceNumber1 || nocData?.online_course?.referenceNumber || "-"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 1. Overview Card → Employee Details Section */}
        <div className="card mb-4">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-user me-2 text-primary"></i>
              Employee Details
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {/* 1. Name */}
              <div className="col-md-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Name</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData.employee
                      ? `${nocData.employee.firstname || ""} ${nocData.employee.lsastname || ""}`.trim()
                      : (nocData?.employeeName || "N/A")}
                  </div>
                </div>
              </div>

              {/* 2. Designation */}
              <div className="col-md-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Designation</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData?.employee?.details
                      ?.sap_department_designation_wings?.designation_name ||
                      nocData?.employee?.designation ||
                      "N/A"}
                  </div>
                </div>
              </div>

              {/* 3. Date of Joining in DMRC */}
              <div className="col-md-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Date of Joining in DMRC</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData.employee?.date_of_joining
                      ? moment.utc(nocData.employee.date_of_joining).format("DD-MM-YYYY")
                      : "N/A"}
                  </div>
                </div>
              </div>

              {/* 4. Employment Status */}
              <div className="col-md-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employment Status</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData?.employee?.employmentType ||
                      nocData?.employee?.details?.employmentType ||
                      (nocData as any)?.employmentType ||
                      nocData?.employee?.employmentStatus ||
                      "N/A"}
                  </div>
                </div>
              </div>

              {/* 5. Active Phone Number */}
              <div className="col-md-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Active Phone Number</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData?.contactNumber || nocData.employee?.phone || "N/A"}
                  </div>
                </div>
              </div>

              {/* 6. Email ID */}
              <div className="col-md-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Email ID</small>
                  <div className="fw-bold text-dark fs-15">
                    {nocData?.email || nocData.employee?.email || "N/A"}
                  </div>
                </div>
              </div>

              {/* 7. Department */}
              <div className="col-md-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Department</small>
                  {nocData?.enabled_fields_fields_json?.Department === "X" || nocData?.enabled_fields_json?.Department === "X" ? (
                    <input
                      type="text"
                      className="form-control"
                      value={getFieldValue(
                        "Department",
                        nocData?.department ||
                        nocData?.dept ||
                        nocData?.departmentName ||
                        nocData?.employee?.details?.sap_department_designation_wings?.department_name ||
                        nocData?.employee?.sap_department_designation_wings?.department_name ||
                        nocData?.employee?.details?.department_name ||
                        nocData?.employee?.department_details?.departmentName ||
                        nocData?.employee?.departmentName ||
                        nocData?.employee?.department ||
                        ""
                      )}
                      onChange={(e) => handleFieldChange("Department", e.target.value)}
                    />
                  ) : (
                    <div className="fw-bold text-dark fs-15">
                      {nocData?.department ||
                        nocData?.dept ||
                        nocData?.departmentName ||
                        nocData?.employee?.details?.sap_department_designation_wings?.department_name ||
                        nocData?.employee?.sap_department_designation_wings?.department_name ||
                        nocData?.employee?.details?.department_name ||
                        nocData?.employee?.department_details?.departmentName ||
                        nocData?.employee?.departmentName ||
                        nocData?.employee?.department ||
                        "N/A"}
                    </div>
                  )}
                </div>
              </div>

              {/* Optional Father's Name if available */}
              {(nocData?.fatherName || nocData?.employee?.details?.father_name) && (
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block uppercase fw-medium mb-1">Father's Name</small>
                    <div className="fw-bold text-dark fs-15">
                      {nocData?.fatherName || nocData?.employee?.details?.father_name || "N/A"}
                    </div>
                  </div>
                </div>
              )}

              {nocType === "NOC_OUTJOB" && (
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block uppercase fw-medium mb-1">Payscale</small>
                    <div className="fw-bold text-dark fs-15">
                      {nocData?.employee?.pay_scale || "N/A"}
                    </div>
                  </div>
                </div>
              )}

              {/* 13. Present Address */}
              {nocData?.presentAddress && (
                <div className="col-md-4">
                  <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Present Address
                  </label>
                  <div className="fw-medium">
                    {nocData?.presentAddress || "N/A"}
                  </div>
                </div>
              )}

              {/* 14. Permanent Address */}
              {nocData?.permanentAddress && (
                <div className="col-md-4">
                  <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Permanent Address
                  </label>
                  <div className="fw-medium">
                    {nocData?.permanentAddress || "N/A"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Previous Remarks Section (for Standard NOC types) */}
        {nocType !== "NOC_ONLINECOURSE-REIMB" && (() => {
          let rawList: any[] = [
            ...(Array.isArray(nocData?.remarks_data) && nocData.remarks_data.length > 0
              ? nocData.remarks_data
              : Array.isArray(nocData?.history) && nocData.history.length > 0
                ? nocData.history
                : Array.isArray(nocData?.work_history) && nocData.work_history.length > 0
                  ? nocData.work_history
                  : Array.isArray(nocData?.remarksHistory) && nocData.remarksHistory.length > 0
                    ? nocData.remarksHistory
                    : [])
          ].map((item: any) => ({ ...item }));

          const applicantId = String(
            nocData?.empId ||
            nocData?.empID ||
            nocData?.employee?.empId ||
            nocData?.employee?.empID ||
            ""
          ).replace(/^0+/, "");

          const hasEmployeeEntry = rawList.some((item: any) => {
            const itemUserId = String(
              item?.approverUserID || item?.userId || item?.empID || ""
            ).replace(/^0+/, "");
            return (
              item?.usertype === "E" ||
              item?.usertype === "Employee" ||
              item?.approverName === "User" ||
              (applicantId && itemUserId && itemUserId === applicantId)
            );
          });

          // Determine employee remarks from any possible field
          const employeeRemarks = (() => {
            const candidates = [
              nocData?.anyRemarks,
              nocData?.any_remarks,
              nocData?.employeeRemarks,
              nocData?.empRemarks,
              nocData?.employee_remarks,
              nocData?.additionalInfo?.remarks,
              nocData?.comments,
              nocData?.Comments,
              nocData?.remarks !== "X" ? nocData?.remarks : null,
              nocData?.Remarks !== "X" ? nocData?.Remarks : null,
              nocData?.hRemarks !== "X" ? nocData?.hRemarks : null,
              nocData?.HRemarks !== "X" ? nocData?.HRemarks : null,
            ];
            for (const cand of candidates) {
              if (
                typeof cand === "string" &&
                cand.trim() &&
                cand.trim() !== "X" &&
                cand.trim() !== "null" &&
                cand.trim() !== "undefined" &&
                cand.trim() !== "-"
              ) {
                return cand.trim();
              }
            }
            return "-";
          })();

          const employeeTimestamp =
            nocData?.requestCreationDate ||
            nocData?.created_at ||
            nocData?.createdAt ||
            nocData?.sap_created_at ||
            nocData?.sapCreatedAt ||
            nocData?.appliedDate ||
            nocData?.submissionDate ||
            nocData?.dateOfApplication ||
            nocData?.requestCreationDat ||
            nocData?.date ||
            nocData?.requestDate ||
            null;

          const employeeName = (() => {
            if (nocData?.employee) {
              const fname = nocData.employee.firstname || nocData.employee.firstName || "";
              const lname = nocData.employee.lsastname || nocData.employee.lastname || nocData.employee.lastName || "";
              const full = `${fname} ${lname}`.trim();
              if (full) return full;
            }
            return nocData?.employeeName || nocData?.empName || "Employee";
          })();

          const employeeUserId =
            nocData?.empId ||
            nocData?.empID ||
            nocData?.employee?.empId ||
            nocData?.employee?.empID ||
            "";

          if (hasEmployeeEntry) {
            rawList = rawList.map((item: any) => {
              const itemUserId = String(
                item?.approverUserID || item?.userId || item?.empID || ""
              ).replace(/^0+/, "");
              const isEmp =
                item?.usertype === "E" ||
                item?.usertype === "Employee" ||
                item?.approverName === "User" ||
                (applicantId && itemUserId && itemUserId === applicantId);
              if (isEmp && !(item?.remarks || item?.remark || item?.Comments) && employeeRemarks !== "-") {
                return { ...item, remarks: employeeRemarks };
              }
              return { ...item };
            });
          } else if (nocData && (employeeUserId || employeeName !== "Employee" || nocData?.id || nocData?.referenceNumber)) {
            rawList.push({
              usertype: "Employee",
              approverUserID: employeeUserId,
              userId: employeeUserId,
              empID: employeeUserId,
              approverName: employeeName,
              userName: employeeName,
              remarks: employeeRemarks,
              createdAt: employeeTimestamp,
              isEmployee: true,
            });
          }

          const validRemarks = rawList;
          if (validRemarks.length === 0) return null;

          // Deduplicate items based on user, remark and timestamp
          const seen = new Set();
          const uniqueRemarks = validRemarks.filter((item: any) => {
            const uid = String(item?.approverUserID || item?.userId || item?.empID || "").trim();
            const rem = String(item?.remarks || item?.remark || "").trim();
            const ts = String(item?.createdAt || item?.created_at || item?.timestamp || item?.date || "").trim();
            const key = `${uid}_${rem}_${ts}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

          // Sort chronologically (newest to oldest) according to timestamp
          const sortedRemarks = [...uniqueRemarks].sort((a: any, b: any) => {
            const timeA = new Date(a?.createdAt || a?.created_at || a?.timestamp || a?.date || 0).getTime();
            const timeB = new Date(b?.createdAt || b?.created_at || b?.timestamp || b?.date || 0).getTime();
            const validTimeA = isNaN(timeA) ? 0 : timeA;
            const validTimeB = isNaN(timeB) ? 0 : timeB;
            if (validTimeA === 0 && validTimeB === 0) return 0;
            if (validTimeA === 0) return 1;
            if (validTimeB === 0) return -1;
            return validTimeB - validTimeA;
          });

          return (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0">
                  <i className="ti ti-history me-2 text-primary"></i>
                  Previous Remarks
                </h5>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>User Type</th>
                        <th>User ID</th>
                        <th>User Name</th>
                        <th>Remarks</th>
                        <th>Time Stamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRemarks.map((history: any, index: number) => {
                        const applicantId = String(nocData?.empId || nocData?.empID || nocData?.employee?.empId || "").replace(/^0+/, "");
                        const historyUserId = String(history?.approverUserID || history?.userId || history?.empID || "").replace(/^0+/, "");

                        const isEmployee =
                          history?.usertype === "E" ||
                          history?.usertype === "Employee" ||
                          history?.approverName === "User" ||
                          (applicantId && historyUserId === applicantId);

                        const userType = isEmployee ? "Employee" : "Approver";

                        const userName = (() => {
                          if (isEmployee) {
                            if (nocData?.employee) {
                              const fname = nocData.employee.firstname || "";
                              const lname = nocData.employee.lsastname || nocData.employee.lastname || "";
                              const full = `${fname} ${lname}`.trim();
                              if (full) return full;
                            }
                            if (history?.userName && history.userName !== "Approver") return history.userName;
                            if (history?.empName) return history.empName;
                            if (history?.approverName && history.approverName !== "User" && history.approverName !== "Approver") return history.approverName;
                            return "Employee";
                          } else {
                            if (history?.approverName && history.approverName !== "User") return history.approverName;
                            if (history?.userName) return history.userName;
                            if (history?.name) return history.name;
                            if (history?.history_approver) {
                              const fname = history.history_approver.firstname || history.history_approver.first_name || "";
                              const lname = history.history_approver.lsastname || history.history_approver.lastname || history.history_approver.last_name || "";
                              const full = `${fname} ${lname}`.trim();
                              if (full) return full;
                            }
                            return "Approver";
                          }
                        })();

                        const displayUserId = formatEmpIdForDisplay(
                          history?.approverUserID || history?.userId || history?.empID || (isEmployee ? nocData?.empId : "")
                        ) || "-";

                        const formattedTimestamp = (() => {
                          const rawDate = history?.createdAt || history?.created_at || history?.timestamp || history?.date;
                          if (!rawDate) return "N/A";
                          if (dayjs(rawDate).isValid()) {
                            return dayjs(rawDate).format("DD-MM-YYYY h:mm A");
                          }
                          const d = new Date(rawDate);
                          if (isNaN(d.getTime())) return "N/A";
                          const day = d.getDate().toString().padStart(2, "0");
                          const month = (d.getMonth() + 1).toString().padStart(2, "0");
                          const year = d.getFullYear();
                          const hours = d.getHours();
                          const minutes = d.getMinutes().toString().padStart(2, "0");
                          const ampm = hours >= 12 ? "PM" : "AM";
                          const displayHour = hours % 12 || 12;
                          return `${day}-${month}-${year} ${displayHour}:${minutes} ${ampm}`;
                        })();

                        return (
                          <tr key={index}>
                            <td>
                              <span className={`badge ${isEmployee ? "bg-primary-subtle text-primary" : "bg-warning-subtle text-warning"} border`}>
                                {userType}
                              </span>
                            </td>
                            <td className="fw-medium">{displayUserId}</td>
                            <td className="fw-medium">{userName}</td>
                            <td>
                              {(history?.remarks || history?.remark || history?.Comments)
                                ? String(history?.remarks || history?.remark || history?.Comments).split("|").pop()?.trim() || "-"
                                : "-"}
                            </td>
                            <td>
                              <small className="text-muted">
                                {formattedTimestamp}
                              </small>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* 3. Application Details (Hidden for NOC_ONLINECOURSE-REIMB which has dedicated HR Compendium + Others sections) */}
        {nocType !== "NOC_ONLINECOURSE-REIMB" && (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="ti ti-briefcase me-2"></i>
                {nocType === "NOC_HIGHERSTUDY"
                  ? "Course Details"
                  : nocType === "NOC_EXINDIA"
                    ? "Travel Details"
                    : nocType === "NOC_ONLINECOURSE"
                      ? "Course Details"
                      : "Application Details"}
              </h5>
            </div>
            <div className="card-body">
              {nocType === "NOC_OUTJOB" ? (
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Whether probation is confirmed in DMRC
                      {/* (Not applicable in case of Absorption, after deputation) */}
                    </label>
                    <div className="fw-medium">
                      {nocData.whetherProbation === "Y"
                        ? "Yes"
                        : nocData.whetherProbation === "N"
                          ? "No"
                          : nocData.whetherProbation === "0"
                            ? "Not Applicable"
                            : "-"}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {nocData.whetherProbation === "0" || nocData.whetherProbation === "Not Applicable"
                        ? "Whether two years service completed after Absorption"
                        : "Whether one year service after probation"}
                    </label>
                    <div className="fw-medium">
                      {(() => {
                        const val = nocData.threeyearsRegularServ || nocData.whetherProbationConfirmed;
                        return val === "Y" || val === "Yes"
                          ? "Yes"
                          : val === "N" || val === "No"
                            ? "No"
                            : val || "-";
                      })()}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Has the Employee Been Imparted Training Abroad by DMRC
                    </label>
                    <div className="fw-medium">
                      {nocData.trainedAbroadByDM === "Y"
                        ? "Yes"
                        : nocData.trainedAbroadByDM === "N"
                          ? "No"
                          : "N/A"}
                    </div>
                  </div>

                  {getFieldStatus("PostAppliedFor") !== "HIDDEN" && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Name of Post Applied For
                      </label>
                      {getFieldStatus("PostAppliedFor") === "EDITABLE" ? (
                        <input
                          type="text"
                          className="form-control"
                          value={getFieldValue("postAppliedFor", nocData.postAppliedFor || nocData.PostAppliedFor || "")}
                          onChange={(e) => handleFieldChange("postAppliedFor", e.target.value)}
                        />
                      ) : (
                        <div className="fw-medium">
                          {nocData.postAppliedFor || nocData.PostAppliedFor || "-"}
                        </div>
                      )}
                    </div>
                  )}



                  {nocData?.trainedAbroadByDM === "Y" && (
                    <>
                      <div className="col-md-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Place of Training Abroad
                        </label>
                        <div className="fw-medium">
                          {nocData?.placeAbroadTrain || "-"}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Year of Training Abroad
                        </label>
                        <div className="fw-medium">
                          {nocData?.yearOfAbroadTrain || "-"}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Place of Posting
                    </label>
                    <div className="fw-medium">
                      {nocData.placeofPosting || "-"}
                    </div>
                  </div>

                  {/* <div className="col-md-4">
                  <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Request Creation Date
                  </label>
                  <div className="fw-medium">
                    {formatDate(nocData.requestCreationDat)}
                  </div>
                </div> */}

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      No. of Application Forwarded in the Year
                    </label>
                    <div className="fw-medium">
                      {getOrdinalNumber(nocData.appNoInYear)}
                    </div>
                  </div>

                  {getFieldStatus("NatureOfOutside") !== "HIDDEN" && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Nature of Outside Job
                      </label>
                      {getFieldStatus("NatureOfOutside") === "EDITABLE" ? (
                        <select
                          className="form-select"
                          value={getFieldValue("natureOfOutside", nocData?.natureDetails?.natureId || nocData?.natureOfOutside || "")}
                          onChange={(e) => handleFieldChange("natureOfOutside", e.target.value)}
                        >
                          <option value="">Select Nature of Job...</option>
                          {jobNatureOptions.map((option) => (
                            <option
                              key={option.sap_master_natures_id || option.sap_master_natures_natureId}
                              value={option.sap_master_natures_natureId}
                            >
                              {option.sap_master_natures_natureTitle}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="fw-medium">
                          {nocData?.natureDetails?.natureTitle || nocData?.natureOfOutside || "-"}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Newspaper/Website
                    </label>
                    <div className="fw-medium">
                      {nocData.newspaperWebsite || "-"}
                    </div>
                  </div>

                  {getFieldStatus("AdvertisementNo") !== "HIDDEN" && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Advertisement No.
                      </label>
                      {getFieldStatus("AdvertisementNo") === "EDITABLE" ? (
                        <input
                          type="text"
                          className="form-control"
                          value={getFieldValue("advertisementNo", nocData.advertisementNo || nocData.advertisementNo1 || "")}
                          onChange={(e) => handleFieldChange("advertisementNo", e.target.value)}
                        />
                      ) : (
                        <div className="fw-medium">
                          {nocData.advertisementNo || nocData.advertisementNo1 || "-"}
                        </div>
                      )}
                    </div>
                  )}

                  {getFieldStatus("AdvertisementDate") !== "HIDDEN" && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Advertisement Date
                      </label>
                      {getFieldStatus("AdvertisementDate") === "EDITABLE" ? (
                        <input
                          type="date"
                          className="form-control"
                          value={getFieldValue("advertisementDate", nocData.advertisementDate || nocData.advertisementDate1 || "")}
                          onChange={(e) => handleFieldChange("advertisementDate", e.target.value)}
                        />
                      ) : (
                        <div className="fw-medium">
                          {nocData.advertisementDate || nocData.advertisementDate1
                            ? formatDate(nocData.advertisementDate || nocData.advertisementDate1)
                            : "-"}
                        </div>
                      )}
                    </div>
                  )}

                  {getFieldStatus("OrganisationInWhich") !== "HIDDEN" && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Name of the Organization in Which Applied
                      </label>
                      {getFieldStatus("OrganisationInWhich") === "EDITABLE" ? (
                        <input
                          type="text"
                          className="form-control"
                          value={getFieldValue("organisationInWhich", nocData.organisationInWhich || nocData.organisation || nocData.OrganisationInWhich || "")}
                          onChange={(e) => handleFieldChange("organisationInWhich", e.target.value)}
                        />
                      ) : (
                        <div className="fw-medium">
                          {nocData.organisationInWhich ||
                            nocData.organisation ||
                            nocData.OrganisationInWhich ||
                            "-"}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Last Date of Receipt of Application
                    </label>
                    <div className="fw-medium">
                      {nocData.lastDateOfApplica
                        ? formatDate(nocData.lastDateOfApplica)
                        : "-"}
                    </div>
                  </div>
                </div>
              ) : nocType === "NOC_HIGHERSTUDY" ? (
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Nature of Duty Performing
                    </label>
                    <div className="fw-medium">
                      {formatNatureOfDuty(nocData?.natureOfDutyPerfo, nocData?.nature_of_duty)}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Name of the Study Course
                    </label>
                    {(nocData?.enabled_fields_json?.CourseName === 'X' || nocData?.enabled_fields_fields_json?.CourseName === 'X') ? (
                      <input
                        type="text"
                        className="form-control"
                        value={getFieldValue("courseName", nocData.courseName || "")}
                        onChange={(e) => handleFieldChange("courseName", e.target.value)}
                      />
                    ) : (
                      <div className="fw-medium">{nocData?.courseName || "-"}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Type of Course
                    </label>
                    <div className="fw-medium">
                      {formatCourseType(nocData)}
                    </div>
                  </div>

                  {nocData?.otherThanDiploma && nocData?.otherThanDiploma !== "other" && (
                    <div className="col-md-6">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Specify Other Type of Course
                      </label>
                      <div className="fw-medium">
                        {nocData.otherThanDiploma}
                      </div>
                    </div>
                  )}

                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Mode of Course
                    </label>
                    {(nocData?.enabled_fields_json?.PartTimeOrCorresp === 'X' || nocData?.enabled_fields_fields_json?.PartTimeOrCorresp === 'X') ? (
                      <select
                        className="form-select"
                        value={getFieldValue("partTimeOrCorresp", nocData.partTimeOrCorresp || nocData.modeOfCourse || "")}
                        onChange={(e) => handleFieldChange("partTimeOrCorresp", e.target.value)}
                      >
                        <option value="">Select Mode...</option>
                        <option value="P">Part Time</option>
                        <option value="C">Correspondence</option>
                        <option value="F">Full Time</option>
                        <option value="R">Regular</option>
                      </select>
                    ) : (
                      <div className="fw-medium">
                        {formatCourseMode(nocData)}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Duration of Course
                    </label>
                    <div className="fw-medium">
                      {nocData?.durationYears || 0}Y {nocData?.monthsCourseDuration || 0}M
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Date of Joining Course
                    </label>
                    <div className="fw-medium">
                      {nocData?.dateOfCourseJoining
                        ? formatDate(nocData.dateOfCourseJoining)
                        : "N/A"}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Institute Name
                    </label>
                    {(nocData?.enabled_fields_json?.Institute === 'X' || nocData?.enabled_fields_fields_json?.Institute === 'X') ? (
                      <input
                        type="text"
                        className="form-control"
                        value={getFieldValue("institute", nocData.institute || "")}
                        onChange={(e) => handleFieldChange("institute", e.target.value)}
                      />
                    ) : (
                      <div className="fw-medium">{nocData?.institute || "N/A"}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Last Date of Registration for the Course
                    </label>
                    <div className="fw-medium">
                      {nocData?.lastDateOfRegistration ? formatDate(nocData.lastDateOfRegistration) : "N/A"}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Course Ending Date (Tentative)
                    </label>
                    <div className="fw-medium">
                      {nocData?.courseEndingDate ? formatDate(nocData.courseEndingDate) : "N/A"}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Proposed Course is Pursuing In
                    </label>
                    <div className="fw-medium">
                      {formatCourseLocation(nocData?.inIndiaOrNot)}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Timing of Classes
                    </label>
                    <div className="fw-medium">{nocData?.timingOfClasses || "N/A"}</div>
                  </div>
                </div>
              ) : nocType === "NOC_EXINDIA" ? (
                <div className="row g-3">


                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Place of Visit
                    </label>
                    {nocData?.enabled_fields_fields_json?.PlaceOfVisitNOC === 'X' || nocData?.enabled_fields_json?.PlaceOfVisitNOC === 'X' ? (
                      <input
                        type="text"
                        className="form-control"
                        value={getFieldValue("PlaceOfVisitNOC", nocData?.placeOfVisitNoc || nocData?.placeOfVisit || "")}
                        onChange={(e) => handleFieldChange("PlaceOfVisitNOC", e.target.value)}
                      />
                    ) : (
                      <div className="fw-medium">
                        {nocData?.placeOfVisitNoc || nocData?.placeOfVisit || "N/A"}
                      </div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Purpose of Visit
                    </label>
                    {nocData?.enabled_fields_fields_json?.PurposeOfVisit === 'X' || nocData?.enabled_fields_json?.PurposeOfVisit === 'X' ? (
                      <input
                        type="text"
                        className="form-control"
                        value={getFieldValue("PurposeOfVisit", nocData?.purposeOfVisit || "")}
                        onChange={(e) => handleFieldChange("PurposeOfVisit", e.target.value)}
                      />
                    ) : (
                      <div className="fw-medium">{nocData?.purposeOfVisit || "N/A"}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Period of Visit From
                    </label>
                    {nocData?.enabled_fields_fields_json?.PeriodOfVisitFrom === 'X' || nocData?.enabled_fields_json?.PeriodOfVisitFrom === 'X' ? (
                      <input
                        type="date"
                        className="form-control"
                        value={getFieldValue("PeriodOfVisitFrom", nocData?.periodOfVisitFrom || "")}
                        onChange={(e) => handleFieldChange("PeriodOfVisitFrom", e.target.value)}
                      />
                    ) : (
                      <div className="fw-medium">{formatDate(nocData?.periodOfVisitFrom) || "N/A"}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Period of Visit To
                    </label>
                    {nocData?.enabled_fields_fields_json?.PeriodOfVisitTo === 'X' || nocData?.enabled_fields_json?.PeriodOfVisitTo === 'X' ? (
                      <input
                        type="date"
                        className="form-control"
                        value={getFieldValue("PeriodOfVisitTo", nocData?.periodOfVisitTo || "")}
                        onChange={(e) => handleFieldChange("PeriodOfVisitTo", e.target.value)}
                      />
                    ) : (
                      <div className="fw-medium">{formatDate(nocData?.periodOfVisitTo) || "N/A"}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Total Members Travelling
                    </label>
                    <div className="fw-medium">{nocData?.totalMembersTravel || "N/A"}</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Period of Leave (From - To)
                    </label>
                    {nocData?.enabled_fields_fields_json?.LeaveDetails === 'X' || nocData?.enabled_fields_json?.LeaveDetails === 'X' ? (
                      <input
                        type="text"
                        className="form-control"
                        value={getFieldValue("LeaveDetails", nocData?.leaveDetails || "")}
                        onChange={(e) => handleFieldChange("LeaveDetails", e.target.value)}
                      />
                    ) : (
                      <div className="fw-medium">{nocData?.leaveDetails || "N/A"}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Staying Expenditure Amount
                    </label>
                    <div className="fw-medium">₹ {Number(nocData?.stayingExpenditure || 0).toLocaleString('en-IN')}</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Travel Expenditure Amount
                    </label>
                    <div className="fw-medium">₹ {Number(nocData?.travelExpenditureA || 0).toLocaleString('en-IN')}</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Other Expenses
                    </label>
                    <div className="fw-medium">₹ {Number(nocData?.otherExpenditureAm || 0).toLocaleString('en-IN')}</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Total Expenditure
                    </label>
                    <div className="fw-bold text-primary">₹ {Number(nocData?.totalExpenditure || 0).toLocaleString('en-IN')}</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>For Travel</label>
                    <div className="fw-medium">
                      {nocData?.noc_travel?.travel_title ||
                        nocData?.tExpenditureBorneByDetails?.travel_title ||
                        (nocData?.tExpenditureBorneBy === "E" ? "Self" :
                          nocData?.tExpenditureBorneBy === "S" ? "Spouse" :
                            nocData?.tExpenditureBorneBy === "O" ? "Others" :
                              nocData?.tExpenditureBorneBy) || "N/A"}
                      {nocData?.tRelativesName ? ` (${nocData.tRelativesName} - ${nocData.tRelationWithPerson || ""})` : ""}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>For Staying</label>
                    <div className="fw-medium">
                      {nocData?.stay_list?.stay_title ||
                        nocData?.sExpenditureBorneByDetails?.stay_title ||
                        (nocData?.sExpenditureBorneBy === "E" ? "Self" :
                          nocData?.sExpenditureBorneBy === "S" ? "Spouse" :
                            nocData?.sExpenditureBorneBy === "O" ? "Others" :
                              nocData?.sExpenditureBorneBy === "H" ? "Hotel" :
                                nocData?.sExpenditureBorneBy) || "N/A"}
                      {nocData?.sRelativesName ? ` (${nocData.sRelativesName} - ${nocData.sRelationWithPerson || ""})` : ""}
                    </div>
                  </div>

                  {/* Travel Entries Itinerary */}
                  {Array.isArray(nocData?.travelEntries) && nocData.travelEntries.length > 0 && (
                    <div className="col-12 mt-3">
                      <label className="form-label text-muted mb-2" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Travel Itinerary & Countries Visited
                      </label>
                      <div className="table-responsive border rounded">
                        <table className="table table-sm align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Seq</th>
                              <th>Country</th>
                              <th>Duration of Visit</th>
                              <th>Purpose of Visit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {nocData.travelEntries.map((tItem: any, idx: number) => (
                              <tr key={idx}>
                                <td>{tItem.seq || idx + 1}</td>
                                <td>{tItem.country || "-"}</td>
                                <td>{tItem.durationOfVisit || "-"}</td>
                                <td>{tItem.purposeOfVisit || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                </div>
              ) : nocType === "NOC_ONLINECOURSE" ? (
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Nature of Duty Performing
                    </label>
                    <div className="fw-medium">
                      {formatNatureOfDuty(nocData?.natureOfDutyPerfo || nocData?.natureOfDuty, nocData?.nature_of_duty)}
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Category of Online Course
                    </label>
                    <div className="fw-medium">
                      {nocData?.course_category?.category_title || "N/A"}
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Course Department
                    </label>
                    <div className="fw-medium">
                      {nocData?.course_category?.course_departments?.department ||
                        nocData?.onlineCourseDepartment ||
                        "N/A"}
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Online Course Name
                    </label>
                    <div className="fw-medium">
                      {nocData?.course_category?.course_departments?.course ||
                        nocData?.onlineCourseName ||
                        "N/A"}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Date of Course Joining
                    </label>
                    <div className="fw-medium">
                      {formatDate(nocData?.dateOfCourseJoining) || "N/A"}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Last Date of Registration
                    </label>
                    <div className="fw-medium">
                      {formatDate(nocData?.lastDateOfRegistration) || "N/A"}
                    </div>
                  </div>
                </div>
              ) : nocType === "NOC_PASSPORT" ? (
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      NOC Applied For
                    </label>
                    <div className="fw-medium">
                      {nocData?.nOCAppliedFor === "0"
                        ? "Fresh Passport"
                        : nocData?.nOCAppliedFor === "1"
                          ? "Renewal of Passport"
                          : nocData?.nOCAppliedFor === "2"
                            ? "Visa"
                            : nocData?.nOCAppliedFor || "N/A"}
                    </div>
                  </div>

                  {(nocData?.nOCAppliedFor === "1" || nocData?.nOCAppliedFor === "2" || nocData?.passportNumber || nocData?.passportNo) && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Passport No.
                      </label>
                      <div className="fw-medium">
                        {nocData?.passportNumber || nocData?.passportNo || "N/A"}
                      </div>
                    </div>
                  )}

                  {(nocData?.nOCAppliedFor === "2" || nocData?.passportType) && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Type of Passport
                      </label>
                      <div className="fw-medium">
                        {nocData?.passportType === "O"
                          ? "Official"
                          : nocData?.passportType === "R"
                            ? "Regular"
                            : nocData?.passportType === "D"
                              ? "Diplomatic"
                              : nocData?.passportType || "N/A"}
                      </div>
                    </div>
                  )}

                  {(nocData?.nOCAppliedFor === "2" || nocData?.placeOfVisitNOC || nocData?.countriesToBeVisited || nocData?.placeOfVisit) && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Countries to be Visited
                      </label>
                      <div className="fw-medium">
                        {nocData?.placeOfVisitNOC || nocData?.countriesToBeVisited || nocData?.placeOfVisit || "N/A"}
                      </div>
                    </div>
                  )}

                  {(nocData?.nOCAppliedFor === "2" || nocData?.purposeOfVisit) && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Purpose of Visit
                      </label>
                      <div className="fw-medium">
                        {nocData?.purposeOfVisit || "N/A"}
                      </div>
                    </div>
                  )}


                  {(nocData?.nOCAppliedFor === "2" || nocData?.durationOfVisit || nocData?.duration_of_visit) && (
                    <div className="col-md-4">
                      <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Duration of Visit
                      </label>
                      <div className="fw-medium">
                        {nocData?.durationOfVisit || nocData?.duration_of_visit || "N/A"}
                      </div>
                    </div>
                  )}

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Pending Case / Court Proceedings
                    </label>
                    <div className="fw-medium">
                      {nocData?.pendingCase === "N" ? "No" : nocData?.pendingCase === "Y" ? "Yes" : nocData?.pendingCase || "No"}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Whether Any D&AR Pending
                    </label>
                    <div className="fw-medium">
                      {nocData?.dnARPending === "N" ? "No" : nocData?.dnARPending === "Y" ? "Yes" : nocData?.dnARPending || "No"}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Whether Any Vigilance Pending
                    </label>
                    <div className="fw-medium">
                      {nocData?.vigilancePending === "N" ? "No" : nocData?.vigilancePending === "Y" ? "Yes" : nocData?.vigilancePending || "No"}
                    </div>
                  </div>

                  {/* Declaration & Note Checklist */}
                  <div className="col-12 mt-3">
                    <div className="alert alert-info py-2 mb-2 px-3 mb-0 small">
                      <i className="ti ti-info-circle me-1"></i>
                      <strong>Note:</strong> Employee should submit a good quality passport size photograph in HR department after final approval in ESS..
                    </div>
                    <div className="p-3 bg-light rounded border">
                      <small className="text-muted d-block uppercase fw-medium mb-2">Declaration & Note</small>
                      <div className="d-flex align-items-center mb-2">
                        <input type="checkbox" className="form-check-input mt-0 me-2" checked disabled />
                        <span className="small text-dark">
                          I certify that I am not involved in any court/police/vigilance case and there is no restriction of court/any authority on foreign travel.
                        </span>
                      </div>

                    </div>

                  </div>
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        )}

        {(nocType === "NOC_ONLINECOURSE" || nocType === "NOC_ONLINECOURSE-REIMB") && (
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-list-details me-2 text-primary"></i>
                Course details as per HR Compendium
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered table-striped mb-0 text-center">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-semibold">Category</th>
                      <th className="fw-semibold">Department</th>
                      <th className="fw-semibold">Online Course Name</th>
                      <th className="fw-semibold">Platform</th>
                      <th className="fw-semibold">Duration of course</th>
                      <th className="fw-semibold">Online course fee</th>
                      <th className="fw-semibold">Contributors</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ backgroundColor: "#ffeba1" }}>
                        {nocData?.online_course?.category?.category_title ||
                          nocData?.online_course?.course_category?.category_title ||
                          reimbParentCourse?.course_category?.category_title ||
                          reimbParentCourse?.onlineCourseCategory ||
                          nocData?.course_category?.category_title ||
                          "-"}
                      </td>
                      <td style={{ backgroundColor: "#ffeba1" }}>
                        {nocData?.online_course?.course_category?.course_departments?.department ||
                          nocData?.online_course?.onlineCourseDepartment ||
                          reimbParentCourse?.course_category?.course_departments?.department ||
                          reimbParentCourse?.onlineCourseDepartment ||
                          nocData?.course_category?.course_departments?.department ||
                          "-"}
                      </td>
                      <td style={{ backgroundColor: "#ffeba1" }}>
                        {nocData?.online_course?.onlineCourseName ||
                          nocData?.online_course?.course_category?.course_departments?.course ||
                          reimbParentCourse?.onlineCourseName ||
                          reimbParentCourse?.course_category?.course_departments?.course ||
                          nocData?.course_category?.course_departments?.course ||
                          "-"}
                      </td>
                      <td style={{ backgroundColor: "#ffeba1" }}>
                        {nocData?.online_course?.course_category?.course_departments?.plateform ||
                          reimbParentCourse?.course_category?.course_departments?.plateform ||
                          reimbParentCourse?.course_category?.course_departments?.platform ||
                          nocData?.course_category?.course_departments?.plateform ||
                          "-"}
                      </td>
                      <td style={{ backgroundColor: "#ffeba1" }}>
                        {nocData?.online_course?.course_category?.course_departments?.duration
                          ? `${nocData.online_course.course_category.course_departments.duration} Weeks`
                          : reimbParentCourse?.course_category?.course_departments?.duration
                            ? `${reimbParentCourse.course_category.course_departments.duration} Weeks`
                            : reimbParentCourse?.durationOfOnlineCourse || nocData?.durationOfOnlineCourse
                              ? `${String(reimbParentCourse?.durationOfOnlineCourse || nocData?.durationOfOnlineCourse).trim()} Weeks`
                              : (nocData?.course_category?.course_departments?.duration || "-")}
                      </td>
                      <td style={{ backgroundColor: "#ffeba1" }}>
                        {nocData?.online_course?.course_category?.course_departments?.course_fee ||
                          nocData?.online_course?.courseFee ||
                          reimbParentCourse?.course_category?.course_departments?.course_fee ||
                          reimbParentCourse?.courseFee ||
                          nocData?.course_category?.course_departments?.course_fee ||
                          "-"}
                      </td>
                      <td style={{ backgroundColor: "#ffeba1" }}>
                        {nocData?.online_course?.course_category?.course_departments?.contributor ||
                          reimbParentCourse?.course_category?.course_departments?.contributor ||
                          nocData?.course_category?.course_departments?.contributor ||
                          "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Previous Reimbursements for Online Courses (Shared for Online Course & Reimbursement) */}
        {(nocType === "NOC_ONLINECOURSE" || nocType === "NOC_ONLINECOURSE-REIMB") && (
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-history me-2 text-primary"></i>
                Previous Reimbursements for online courses
              </h5>
            </div>
            <div className="card-body">
              <div className="table-responsive border rounded">
                <table className="table table-sm table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Category</th>
                      <th>Platform</th>
                      <th>Duration of course</th>
                      <th>Course Name</th>
                      <th>Date of joining course</th>
                      <th>Date of Completion</th>
                      <th>Online Course Fee</th>
                      <th>Date of Reimbursement</th>
                      <th>End of Block</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(nocData?.previousReimbursements || nocData?.previous_reimbursements || nocData?.reimbursement_history) &&
                      (nocData?.previousReimbursements || nocData?.previous_reimbursements || nocData?.reimbursement_history).length > 0 ? (
                      (nocData?.previousReimbursements || nocData?.previous_reimbursements || nocData?.reimbursement_history).map((item: any, idx: number) => (
                        <tr key={idx}>
                          <td>{item.category || item.category_title || "-"}</td>
                          <td>{item.platform || "-"}</td>
                          <td>{item.duration || item.durationOfOnlineCourse || "-"}</td>
                          <td>{item.courseName || item.course_name || "-"}</td>
                          <td>{formatDate(item.dateOfJoining || item.dateOfCourseJoining) || "-"}</td>
                          <td>{formatDate(item.dateOfCompletion || item.courseCompletionDate) || "-"}</td>
                          <td>{item.fee || item.courseFee || "-"}</td>
                          <td>{formatDate(item.dateOfReimbursement) || "-"}</td>
                          <td>{formatDate(item.endOfBlockDate) || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-start py-2 ps-3 text-dark" style={{ backgroundColor: "#fff3cd" }}>
                          <i className="ti ti-info-circle me-2 text-warning"></i>
                          No Reimbursement taken for Online course previously
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {nocType === "NOC_ONLINECOURSE" ? (
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-briefcase me-2 text-primary"></i>
                Others
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-12 align-items-start">
                  <label className="form-label text-muted">
                    <h6>Instructions</h6>
                  </label>
                  <div
                    style={{
                      background: "#f2f2f2",
                      padding: "20px",
                      borderRadius: "5px",
                    }}
                  >
                    <ol style={{ lineHeight: "2.4" }}>
                      <li>
                        1. For field "Web URL" please provide link to programme
                        brochure/website URL, having details of fee, last date
                        of registration etc.
                      </li>
                      <li>
                        2. Updated course fee is to be filled if course fee is
                        currently different from the approved amount in the HR
                        Compendium.
                      </li>
                      <li>
                        3. If fee is in dollar/other currency, please provide
                        amount in INR.
                      </li>
                      <li>
                        ⁠4. If there is no fixed Last Registration Date then
                        please enter today's date
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="row g-3" style={{ padding: "20px 0" }}>
                <div className="col-md-6 d-flex align-items-start">
                  <label className="form-label text-muted fw-semibold">Web Url :</label>
                  <div className="ms-2">
                    {nocData?.courseBrochure ? (
                      <a href={nocData.courseBrochure} target="_blank" rel="noreferrer" className="text-primary text-break">
                        {nocData.courseBrochure}
                      </a>
                    ) : "N/A"}
                  </div>
                </div>

                <div className="col-md-6 d-flex align-items-start">
                  <label className="form-label text-muted fw-semibold">
                    Updated Course Fee :
                  </label>
                  <div className="ms-2">
                    {`${nocData?.courseFee || "0"} ${nocData?.currency || "INR"}`}
                  </div>
                </div>

                <div className="col-md-6 d-flex align-items-start">
                  <label className="form-label text-muted fw-semibold">
                    Last date of registration of course:
                  </label>
                  <div className="ms-2">
                    {formatDate(nocData?.lastDateOfRegistration) || "N/A"}
                  </div>
                </div>

                <div className="col-md-6 d-flex align-items-start">
                  <label className="form-label text-muted fw-semibold">
                    Probable date of commencement of course:
                  </label>
                  <div className="ms-2">
                    {formatDate(nocData?.dateOfCourseJoining) || "N/A"}
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-2">
                <div className="col-md-12 d-flex align-items-start">
                  <label className="form-label text-muted fw-semibold">Any Remarks :</label>
                  <div className="ms-2">{nocData?.anyRemarks || nocData?.remarks || "N/A"}</div>
                </div>
              </div>
            </div>
          </div>
        ) : null}


        {/* 4. Education Details */}
        {nocType === "NOC_OUTJOB" && nocData.education && nocData.education.length > 0 && (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="ti ti-school me-2"></i>
                Education Details
              </h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Course Name</th>
                      <th>Date of Admission</th>
                      <th>Date of Passing</th>
                      <th>Qualification Acquired</th>
                      <th>Course Affiliated by UGC/AICTE</th>
                      <th>Documents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nocData.education.map((edu: any, index: number) => (
                      <tr key={index}>
                        <td className="fw-medium">{edu.courseName}</td>
                        <td>
                          <small className="text-muted">
                            {formatDate(edu.courseDateOfAdmission)}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {formatDate(edu.courseDateOfPassing)}
                          </small>
                        </td>
                        <td>
                          <span
                            className={`badge ${edu.qualificationAcquir === "A"
                              ? "bg-info"
                              : "bg-warning"
                              }`}
                          >
                            {edu.qualificationAcquir === "A"
                              ? "After Joining"
                              : edu.qualificationAcquir === "B"
                                ? "Before Joining"
                                : "N/A"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${formatBoolean(edu.isCourseRecog) === "Yes"
                              ? "bg-success"
                              : "bg-secondary"
                              }`}
                          >
                            {formatBoolean(edu.isCourseRecog)}
                          </span>
                        </td>
                        <td>
                          {edu.edu_documents && edu.edu_documents.length > 0 ? (
                            <div className="d-flex flex-wrap gap-1">
                              {edu.edu_documents
                                .filter((d: any) => d.referenceNumber?.toString().length < 20)
                                .map((doc: any, docIdx: number) => (
                                  <button
                                    key={docIdx}
                                    type="button"
                                    className="btn btn-xs btn-outline-primary py-0 px-2"
                                    style={{ fontSize: "0.75rem" }}
                                    onClick={() => handleDocumentClick(doc)}
                                    title="View document"
                                  >
                                    <i className="ti ti-eye me-1"></i>
                                    {doc.typeOfAttachedDoc === "N"
                                      ? "NOC/Office Order"
                                      : doc.typeOfAttachedDoc === "E"
                                        ? "Certificate"
                                        : `Doc ${docIdx + 1}`}
                                  </button>
                                ))}
                            </div>
                          ) : (
                            <span className="text-muted">None</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 5. Documents */}
        {(() => {
          const mainDocs = Array.isArray(nocData.documents) ? nocData.documents : [];
          const eduDocs: any[] = [];
          if (Array.isArray(nocData.education)) {
            nocData.education.forEach((edu: any) => {
              if (Array.isArray(edu.edu_documents)) {
                eduDocs.push(...edu.edu_documents);
              }
            });
          }
          const combined = [...mainDocs, ...eduDocs];
          const allNocDocuments = combined
            .filter(
              (item: any, idx: number, self: any[]) =>
                idx === self.findIndex((t: any) => (t.id && item.id ? t.id === item.id : t.fileName === item.fileName))
            )
            .filter(
              (item: any) => !item.referenceNumber || item.referenceNumber?.toString().length < 20
            );

          if (!allNocDocuments || allNocDocuments.length === 0) return null;

          return (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="ti ti-files me-2"></i>
                  Attachments ({allNocDocuments.length})
                </h5>
              </div>
              <div className="card-body">
                {/* Mandatory Attachments Info Card */}
                <div className="alert alert-danger bg-danger-transparent border-danger mb-3 p-3">
                  <h6 className="text-danger mb-2">
                    <i className="ti ti-info-circle me-2"></i>
                    Mandatory Attachments
                  </h6>
                  <ul className="mb-0 ps-3 small text-muted">
                    {nocType === "NOC_OUTJOB" ? (
                      <>
                        <li>- Copy of advertisement indicating last date of application and post applied for must be attached.</li>
                        <li>- If, with the existing educational qualifications (as captured in SAP), the employee is not eligible for the post, uploading requisite educational qualification documents is mandatory, on basis of which the employee is eligible for the post that he/she intends to apply for.</li>
                        <li> Documents to be uploaded - Final Marksheet/Certificate</li>
                      </>
                    ) : nocType === "NOC_HIGHERSTUDY" ? (
                      <>
                        <li>- In case any other NOC has been granted for pursuing higher studies during course of employment in DMRC attach copy of NOC</li>
                        <li>- Document indicating last date of registration of course must be attached.</li>
                        <li>- Last date of admission in brochure</li>
                      </>
                    ) : nocType === "NOC_EXINDIA" || nocType === "NOC_PASSPORT" ? (
                      <>
                        <li className="mb-2">
                          - In case of expense are borne by employee himself/spouse,
                          proof of saving like last six months bank statement is to be
                          provided.
                        </li>
                        <li className="mb-2">
                          - In case of stay travel arrangement made by relative abroad,
                          the written declaration for the same is to be attached.
                        </li>
                        <li className="mb-2">
                          - In case of stay travel arrangement made by relative abroad,
                          proof of domicile is to be attached.
                        </li>
                        <li className="mb-0">
                          - Screenshot of approved/sanctioned leave requests for
                          ex-India must be attached.
                        </li>
                      </>
                    ) : nocType === "NOC_ONLINECOURSE-REIMB" ? (
                      <>
                        <li>- Completion Certificate (Mandatory)</li>
                      </>
                    ) : (
                      <>
                        <li>- Course Brochure / Details Document</li>
                        <li>- Proof of Registration / Fee Receipt (if applicable)</li>
                      </>
                    )}
                  </ul>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>File Name</th>
                        <th>Type</th>
                        <th>Reference Number</th>
                        <th>Upload Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allNocDocuments.map((doc: any, index: number) => (
                        <tr key={index}>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className="ti ti-file me-2 text-primary"></i>
                              <span className="fw-medium">
                                {doc.fileName ? doc.fileName.split("/").pop() : "Unnamed Document"}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge ${doc.typeOfAttachedDoc === "Qualification" || doc.typeOfAttachedDoc === "E" || doc.typeOfAttachedDoc === "03" || doc.typeOfAttachedDoc === "3"
                                ? "bg-success"
                                : doc.typeOfAttachedDoc === "NOC/Addition" || doc.typeOfAttachedDoc === "N" || doc.typeOfAttachedDoc === "01" || doc.typeOfAttachedDoc === "1"
                                  ? "bg-info"
                                  : "bg-secondary"
                                }`}
                            >
                              {doc.typeOfAttachedDoc === "01" || doc.typeOfAttachedDoc === "1"
                                ? "Fee Receipt"
                                : doc.typeOfAttachedDoc === "02" || doc.typeOfAttachedDoc === "2"
                                  ? "Proof of Payment in INR"
                                  : doc.typeOfAttachedDoc === "03" || doc.typeOfAttachedDoc === "3"
                                    ? "Course Completion Certificate"
                                    : doc.typeOfAttachedDoc === "N"
                                      ? "NOC or Addition Office Order"
                                      : doc.typeOfAttachedDoc === "E"
                                        ? "Education/Qualification Certificates"
                                        : doc.typeOfAttachedDoc === "A"
                                          ? "Advertisement for Outside Job"
                                          : doc.typeOfAttachedDoc || "General"}
                            </span>
                          </td>
                          <td>
                            <span className="font-monospace small text-muted">
                              {doc.referenceNumber || nocData.referenceNumber || "-"}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatDate(doc.created_at)}
                            </small>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleDocumentClick(doc)}
                              title="View / Download File"
                              disabled={!doc.fileName && !doc.UUID && !doc.docID}
                            >
                              <i className="ti ti-eye me-1"></i>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Previous Remarks Section for NOC_ONLINECOURSE-REIMB */}
        {nocType === "NOC_ONLINECOURSE-REIMB" && (() => {
          let rawList: any[] = [
            ...(Array.isArray(nocData?.remarks_data) && nocData.remarks_data.length > 0
              ? nocData.remarks_data
              : Array.isArray(nocData?.history) && nocData.history.length > 0
                ? nocData.history
                : Array.isArray(nocData?.work_history) && nocData.work_history.length > 0
                  ? nocData.work_history
                  : Array.isArray(nocData?.remarksHistory) && nocData.remarksHistory.length > 0
                    ? nocData.remarksHistory
                    : [])
          ].map((item: any) => ({ ...item }));

          const applicantId = String(
            nocData?.empId ||
            nocData?.empID ||
            nocData?.employee?.empId ||
            nocData?.employee?.empID ||
            ""
          ).replace(/^0+/, "");

          const hasEmployeeEntry = rawList.some((item: any) => {
            const itemUserId = String(
              item?.approverUserID || item?.userId || item?.empID || ""
            ).replace(/^0+/, "");
            return (
              item?.usertype === "E" ||
              item?.usertype === "Employee" ||
              item?.approverName === "User" ||
              (applicantId && itemUserId && itemUserId === applicantId)
            );
          });

          // Determine employee remarks from any possible field
          const employeeRemarks = (() => {
            const candidates = [
              nocData?.anyRemarks,
              nocData?.any_remarks,
              nocData?.employeeRemarks,
              nocData?.empRemarks,
              nocData?.employee_remarks,
              nocData?.additionalInfo?.remarks,
              nocData?.comments,
              nocData?.Comments,
              nocData?.remarks !== "X" ? nocData?.remarks : null,
              nocData?.Remarks !== "X" ? nocData?.Remarks : null,
              nocData?.hRemarks !== "X" ? nocData?.hRemarks : null,
              nocData?.HRemarks !== "X" ? nocData?.HRemarks : null,
            ];
            for (const cand of candidates) {
              if (
                typeof cand === "string" &&
                cand.trim() &&
                cand.trim() !== "X" &&
                cand.trim() !== "null" &&
                cand.trim() !== "undefined" &&
                cand.trim() !== "-"
              ) {
                return cand.trim();
              }
            }
            return "-";
          })();

          const employeeTimestamp =
            nocData?.requestCreationDate ||
            nocData?.created_at ||
            nocData?.createdAt ||
            nocData?.sap_created_at ||
            nocData?.sapCreatedAt ||
            nocData?.appliedDate ||
            nocData?.submissionDate ||
            nocData?.dateOfApplication ||
            nocData?.requestCreationDat ||
            nocData?.date ||
            nocData?.requestDate ||
            null;

          const employeeName = (() => {
            if (nocData?.employee) {
              const fname = nocData.employee.firstname || nocData.employee.firstName || "";
              const lname = nocData.employee.lsastname || nocData.employee.lastname || nocData.employee.lastName || "";
              const full = `${fname} ${lname}`.trim();
              if (full) return full;
            }
            return nocData?.employeeName || nocData?.empName || "Employee";
          })();

          const employeeUserId =
            nocData?.empId ||
            nocData?.empID ||
            nocData?.employee?.empId ||
            nocData?.employee?.empID ||
            "";

          if (hasEmployeeEntry) {
            rawList = rawList.map((item: any) => {
              const itemUserId = String(
                item?.approverUserID || item?.userId || item?.empID || ""
              ).replace(/^0+/, "");
              const isEmp =
                item?.usertype === "E" ||
                item?.usertype === "Employee" ||
                item?.approverName === "User" ||
                (applicantId && itemUserId && itemUserId === applicantId);
              if (isEmp && !(item?.remarks || item?.remark || item?.Comments) && employeeRemarks !== "-") {
                return { ...item, remarks: employeeRemarks };
              }
              return { ...item };
            });
          } else if (nocData && (employeeUserId || employeeName !== "Employee" || nocData?.id || nocData?.referenceNumber)) {
            rawList.push({
              usertype: "Employee",
              approverUserID: employeeUserId,
              userId: employeeUserId,
              empID: employeeUserId,
              approverName: employeeName,
              userName: employeeName,
              remarks: employeeRemarks,
              createdAt: employeeTimestamp,
              isEmployee: true,
            });
          }

          const validRemarks = rawList;
          if (validRemarks.length === 0) return null;

          // Deduplicate items based on user, remark and timestamp
          const seen = new Set();
          const uniqueRemarks = validRemarks.filter((item: any) => {
            const uid = String(item?.approverUserID || item?.userId || item?.empID || "").trim();
            const rem = String(item?.remarks || item?.remark || "").trim();
            const ts = String(item?.createdAt || item?.created_at || item?.timestamp || item?.date || "").trim();
            const key = `${uid}_${rem}_${ts}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

          // Sort chronologically (newest to oldest) according to timestamp
          const sortedRemarks = [...uniqueRemarks].sort((a: any, b: any) => {
            const timeA = new Date(a?.createdAt || a?.created_at || a?.timestamp || a?.date || 0).getTime();
            const timeB = new Date(b?.createdAt || b?.created_at || b?.timestamp || b?.date || 0).getTime();
            const validTimeA = isNaN(timeA) ? 0 : timeA;
            const validTimeB = isNaN(timeB) ? 0 : timeB;
            if (validTimeA === 0 && validTimeB === 0) return 0;
            if (validTimeA === 0) return 1;
            if (validTimeB === 0) return -1;
            return validTimeB - validTimeA;
          });

          return (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0">
                  <i className="ti ti-history me-2 text-primary"></i>
                  Previous Remarks
                </h5>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>User Type</th>
                        <th>User ID</th>
                        <th>User Name</th>
                        <th>Remarks</th>
                        <th>Time Stamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRemarks.map((history: any, index: number) => {
                        const applicantId = String(nocData?.empId || nocData?.empID || nocData?.employee?.empId || "").replace(/^0+/, "");
                        const historyUserId = String(history?.approverUserID || history?.userId || history?.empID || "").replace(/^0+/, "");

                        const isEmployee =
                          history?.usertype === "E" ||
                          history?.usertype === "Employee" ||
                          history?.approverName === "User" ||
                          (applicantId && historyUserId === applicantId);

                        const userType = isEmployee ? "Employee" : "Approver";

                        const userName = (() => {
                          if (isEmployee) {
                            if (nocData?.employee) {
                              const fname = nocData.employee.firstname || "";
                              const lname = nocData.employee.lsastname || nocData.employee.lastname || "";
                              const full = `${fname} ${lname}`.trim();
                              if (full) return full;
                            }
                            if (history?.userName && history.userName !== "Approver") return history.userName;
                            if (history?.empName) return history.empName;
                            if (history?.approverName && history.approverName !== "User" && history.approverName !== "Approver") return history.approverName;
                            return "Employee";
                          } else {
                            if (history?.approverName && history.approverName !== "User") return history.approverName;
                            if (history?.userName) return history.userName;
                            if (history?.name) return history.name;
                            if (history?.history_approver) {
                              const fname = history.history_approver.firstname || history.history_approver.first_name || "";
                              const lname = history.history_approver.lsastname || history.history_approver.lastname || history.history_approver.last_name || "";
                              const full = `${fname} ${lname}`.trim();
                              if (full) return full;
                            }
                            return "Approver";
                          }
                        })();

                        const displayUserId = formatEmpIdForDisplay(
                          history?.approverUserID || history?.userId || history?.empID || (isEmployee ? nocData?.empId : "")
                        ) || "-";

                        const formattedTimestamp = (() => {
                          const rawDate = history?.createdAt || history?.created_at || history?.timestamp || history?.date;
                          if (!rawDate) return "N/A";
                          if (dayjs(rawDate).isValid()) {
                            return dayjs(rawDate).format("DD-MM-YYYY h:mm A");
                          }
                          const d = new Date(rawDate);
                          if (isNaN(d.getTime())) return "N/A";
                          const day = d.getDate().toString().padStart(2, "0");
                          const month = (d.getMonth() + 1).toString().padStart(2, "0");
                          const year = d.getFullYear();
                          const hours = d.getHours();
                          const minutes = d.getMinutes().toString().padStart(2, "0");
                          const ampm = hours >= 12 ? "PM" : "AM";
                          const displayHour = hours % 12 || 12;
                          return `${day}-${month}-${year} ${displayHour}:${minutes} ${ampm}`;
                        })();

                        return (
                          <tr key={index}>
                            <td>
                              <span className={`badge ${isEmployee ? "bg-primary-subtle text-primary" : "bg-warning-subtle text-warning"} border`}>
                                {userType}
                              </span>
                            </td>
                            <td className="fw-medium">{displayUserId}</td>
                            <td className="fw-medium">{userName}</td>
                            <td>
                              {(history?.remarks || history?.remark || history?.Comments)
                                ? String(history?.remarks || history?.remark || history?.Comments).split("|").pop()?.trim() || "-"
                                : "-"}
                            </td>
                            <td>
                              <small className="text-muted">
                                {formattedTimestamp}
                              </small>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Others & Notes Section for NOC_ONLINECOURSE-REIMB */}
        {nocType === "NOC_ONLINECOURSE-REIMB" && (
          <>
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0">
                  <i className="ti ti-apps me-2 text-primary"></i>
                  Others
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block text-uppercase fw-medium mb-1">Request Type</small>
                      <div className="fw-bold text-dark fs-15">
                        {nocData?.reimbursement_type?.type_title || (nocData?.claimOrOnlyAdditi === "AD" ? "Only Addition of record in system" : "Reimbursement and Addition of record in system")}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block text-uppercase fw-medium mb-1">Block Year Ending On</small>
                      <div className="fw-bold text-dark fs-15">
                        {nocData?.endOfBlockDate && nocData?.endOfBlockDate !== "00-00-0000" ? formatDate(nocData.endOfBlockDate) : "-"}
                      </div>
                    </div>
                  </div>

                  {(nocData?.claimOrOnlyAdditi === "CL" || nocData?.reimbursement_type?.type_id === "CL") && (
                    <>
                      <div className="col-md-4">
                        <div className="p-3 bg-light rounded border h-100">
                          <small className="text-muted d-block text-uppercase fw-medium mb-1">
                            Amount Remaining for Current Block Year (out of ₹18000)
                          </small>
                          <div className="fw-bold text-dark fs-15">
                            {nocData?.eligibilityAmount ? `₹ ${parseFloat(nocData.eligibilityAmount).toFixed(3)} /-` : "₹ 18000 /-"}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="p-3 bg-light rounded border h-100">
                          <small className="text-muted d-block text-uppercase fw-medium mb-1">Requested Amount</small>
                          <div className="fw-bold text-primary fs-15">
                            {`₹ ${parseFloat(nocData?.requestAmount || "0").toFixed(3)} /-`}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="col-md-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block text-uppercase fw-medium mb-1">Date of Joining Online Course</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDate(nocData?.dOJOnlineCourse) || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block text-uppercase fw-medium mb-1">Date of Completion of Course</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDate(nocData?.courseCompletionDate) || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block text-uppercase fw-medium mb-1">Duration of Course</small>
                      <div className="fw-bold text-dark fs-15">
                        {nocData?.durationOfOnlineCourse ? `${nocData.durationOfOnlineCourse} Weeks` : (nocData?.online_course?.durationOfOnlineCourse || "-")}
                      </div>
                    </div>
                  </div>

                  {(nocData?.claimOrOnlyAdditi === "CL" ||
                    nocData?.reimbursement_type?.type_id === "CL") && (
                      <div className="col-md-12">
                        <div className="p-3 bg-light rounded border">
                          <label className="form-label mb-1 fw-bold text-dark">
                            Approved Amount (₹) : <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="form-control"
                            placeholder="Enter Approved Amount in INR"
                            onChange={(e) => setApprovedAmount(e.target.value)}
                            required
                            value={approvedAmount}
                            disabled={
                              nocData?.enabled_fields_json?.ApprovedAmount === "disabled" ||
                              (nocData?.requestStatus &&
                                nocData?.requestStatus !== "T" &&
                                nocData?.requestStatus !== "P" &&
                                nocData?.requestStatus !== "Pending" &&
                                nocData?.requestStatus !== "SUBMITTED")
                            }
                          />
                        </div>
                      </div>
                    )}

                  <div className="col-md-12">
                    <div className="p-3 bg-light rounded border">
                      <label className="form-label mb-1 fw-bold text-dark">
                        Remarks
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Enter remarks"
                        value={hRemarks}
                        onChange={(e) => setHRemarks(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes Section for Reimbursement / Addition */}
            <div className="card mb-4 border-info">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0 text-dark">
                  <i className="ti ti-info-circle me-2 text-info"></i>
                  Notes
                </h5>
              </div>
              <div className="card-body">
                <ul className="mb-0 text-dark small ps-3">
                  <li>• Reimbursement request should be applied within 6 months of completion of the course.</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Other Card (D&AR/Vigilance Penalty 10 years) for NOC_OUTJOB */}
        {nocType === "NOC_OUTJOB" && (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="ti ti-shield-check me-2"></i>
                Other
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Whether Any Penalty Imposed, During Last 10 Years In Any D&AR Case
                  </label>
                  <div className="fw-medium">
                    {(() => {
                      const dar = nocData?.dandARPendingOrNot || nocData?.DandARPendingOrNot || nocData?.dAndARPendingOrNot || nocData?.dnARPendingOrNot || nocData?.dygarCasePenalty || nocData?.otherDetails?.darPending || "";
                      const isYes = dar === "Y" || dar === "Yes" || dar === "y" || dar === "yes";
                      const isNo = dar === "N" || dar === "No" || dar === "n" || dar === "no" || dar === "" || dar === null || dar === undefined;
                      return (
                        <span className={`badge ${isYes ? "bg-danger" : isNo ? "bg-success" : "bg-secondary"}`}>
                          {isYes ? "Yes" : isNo ? "No" : "N/A"}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label text-muted mb-1" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Whether Any Penalty Imposed, During Last 10 Years In Any Vigilance Case
                  </label>
                  <div className="fw-medium">
                    {(() => {
                      const vig = nocData?.vigilancePending || nocData?.VigilancePending || nocData?.vigilanceCasePenalty || nocData?.otherDetails?.vigilancePending || "";
                      const isYes = vig === "Y" || vig === "Yes" || vig === "y" || vig === "yes";
                      const isNo = vig === "N" || vig === "No" || vig === "n" || vig === "no" || vig === "" || vig === null || vig === undefined;
                      return (
                        <span className={`badge ${isYes ? "bg-danger" : isNo ? "bg-success" : "bg-secondary"}`}>
                          {isYes ? "Yes" : isNo ? "No" : "N/A"}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Employee Education Data Card for HR Approvers in NOC_OUTJOB (or when ApproverLevel > 1) */}
        {(() => {
          const localUser = (() => {
            try {
              const raw = localStorage.getItem("user");
              if (!raw || raw === "undefined") return {};
              const parsed = JSON.parse(raw);
              return parsed?.data?.user || parsed?.user || parsed || {};
            } catch { return {}; }
          })();
          const deptName = localUser?.details?.sap_department_designation_wings?.department_name || "";
          const isHRDept = deptName.includes("HR") || deptName.includes("HUMAN RESOURCE");
          const approverLevel = parseInt(nocData?.ApproverLevel || nocData?.approverLevel || "0");
          const shouldShow = nocType === "NOC_OUTJOB" && isHRDept && approverLevel > 1 && empEduData && empEduData.length > 0;
          return shouldShow ? (
            <div className="card mb-4">
              <div className="card-header bg-light d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">
                  <i className="ti ti-school me-2 text-primary"></i>
                  Employee Education Data
                </h5>
                {isEmpEduLoading && (
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                )}
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle mb-0">
                    <thead className="table-light">

                      <tr>
                        <th style={{ width: "50px" }} className="text-center">#</th>
                        <th>Education</th>
                        <th>Certificate</th>
                        <th>Course Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empEduData.map((item: any, index: number) => (
                        <tr key={item.id || index}>
                          <td className="text-center fw-medium">{index + 1}</td>
                          <td>{item.education_text || "-"}</td>
                          <td>{item.courseName1 || "-"}</td>
                          <td>{item.courseName2 || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null;
        })()}

        {/* Declaration Card for NOC_OUTJOB */}
        {nocType === "NOC_OUTJOB" && (
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-certificate me-2 text-primary"></i>
                Declaration
              </h5>
            </div>
            <div className="card-body">
              <ul className="mb-0 text-muted small ps-3">
                <li className="mb-2">1. I hereby declare that the information given above is true.</li>
                <li className="mb-2">2. I declared that I am prima facie meeting eligibility criteria (education qualification, age, experience & pay-scale etc.) for the post, I intend to apply for, as per the enclosed(uploaded) advertisement.</li>
                {(nocData?.natureDetails?.natureTitle === "Deputation" || nocData?.natureOfOutsideJob === "Deputation" || nocData?.natureOfOutside === "Deputation") && (
                  <li>3. Terms and conditions of deputation as applicable.</li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Questions & Declaration Card for NOC_HIGHERSTUDY */}
        {nocType === "NOC_HIGHERSTUDY" && (
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-help-circle me-2 text-primary"></i>
                Questions & Declaration
              </h5>
            </div>
            <div className="card-body">
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-dark fw-medium" style={{ fontSize: "14px" }}>
                    Whether probation is confirmed in DMRC
                  </span>
                  <span className={`badge ${nocData?.probationConfirmed?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                    {nocData?.probationConfirmed?.toUpperCase() === "Y" ? "Yes" : nocData?.probationConfirmed?.toUpperCase() === "N" ? "No" : "N/A"}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-dark fw-medium" style={{ fontSize: "14px" }}>
                    Whether currently pursuing any higher education course, for which NOC from DMRC
                  </span>
                  <span className={`badge ${nocData?.currentlyPursuingA?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                    {nocData?.currentlyPursuingA?.toUpperCase() === "Y" ? "Yes" : nocData?.currentlyPursuingA?.toUpperCase() === "N" ? "No" : "N/A"}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-dark fw-medium" style={{ fontSize: "14px" }}>
                    Whether the institution is approved by concerned Government Body like UGC/AICTE/DEC
                  </span>
                  <span className={`badge ${nocData?.isGovernmentBody?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                    {nocData?.isGovernmentBody?.toUpperCase() === "Y" ? "Yes" : nocData?.isGovernmentBody?.toUpperCase() === "N" ? "No" : "N/A"}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-dark fw-medium" style={{ fontSize: "14px" }}>
                    Whether the Course is approved by concerned Government Body like UGC/AICTE/DEC
                  </span>
                  <span className={`badge ${nocData?.isCourseRecogByGov?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                    {nocData?.isCourseRecogByGov?.toUpperCase() === "Y" ? "Yes" : nocData?.isCourseRecogByGov?.toUpperCase() === "N" ? "No" : "N/A"}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-dark fw-medium" style={{ fontSize: "14px" }}>
                    Is the course relevant to your employment
                  </span>
                  <span className={`badge ${nocData?.isReleventToEmployee?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                    {nocData?.isReleventToEmployee?.toUpperCase() === "Y" ? "Yes" : nocData?.isReleventToEmployee?.toUpperCase() === "N" ? "No" : "N/A"}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-dark fw-medium" style={{ fontSize: "14px" }}>
                    Whether any other NOC has been granted during employment in DMRC for any other course
                  </span>
                  <span className={`badge ${nocData?.whetherAnotherNoc?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                    {nocData?.whetherAnotherNoc?.toUpperCase() === "Y" ? "Yes" : nocData?.whetherAnotherNoc?.toUpperCase() === "N" ? "No" : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Declaration Card for NOC_HIGHERSTUDY */}
        {nocType === "NOC_HIGHERSTUDY" && (
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-file-check me-2 text-primary"></i>
                Declaration
              </h5>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-start gap-3">
                <input
                  type="checkbox"
                  className="form-check-input mt-1"
                  id="declarationCheckboxHigher"
                  checked={true}
                  disabled={true}
                  readOnly={true}
                  style={{
                    width: "20px",
                    height: "20px",
                    cursor: "not-allowed",
                  }}
                />
                <label
                  className="form-check-label text-dark"
                  htmlFor="declarationCheckboxHigher"
                  style={{ fontSize: "14px", lineHeight: "1.6", cursor: "default" }}
                >
                  I hereby declare that the information given above is true and my duty hours in DMRC would not get disturbed in any case. I may kindly be granted permission to pursue further studies.
                </label>
              </div>
            </div>
          </div>
        )}

        {/* 6. D&AR Status Section */}
        {(() => {
          if (nocType === "NOC_HIGHERSTUDY") return null;

          if (nocType === "NOC_ONLINECOURSE" || nocType === "NOC_ONLINECOURSE-REIMB") {
            const hasRemarks1 =
              nocData?.enabled_fields_json?.Remarks1 === "X" ||
              nocData?.enabled_fields_fields_json?.Remarks1 === "X" ||
              nocData?.Remarks1 === "X";
            if (!hasRemarks1) return null;
            return (
              <div className="card mb-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0">
                    <i className="ti ti-message-dots me-2 text-primary"></i>
                    Remarks
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-bold">Remarks (if any):</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter remarks (if any)"
                        value={remarks !== "" ? remarks : ((nocData?.Remarks !== "X" && nocData?.Remarks) || (nocData?.remarks !== "X" && nocData?.remarks) || (nocData?.Remarks1 !== "X" && nocData?.Remarks1) || (nocData?.remarks1 !== "X" && nocData?.remarks1) || "")}
                        onChange={(e) => setRemarks(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          const currentDnarStatus = dnARStatus || nocData?.dnARStatus || nocData?.DnARStatus || nocData?.dandARStatus || nocData?.DandARStatus || "";
          const penaltyDate = dnARPenaltyEffective || nocData?.dnARPenaltyEffecti || nocData?.DnARPenaltyEffecti || nocData?.dandARPenaltyEffective || nocData?.DandARPenaltyEffective || "";
          const controllingRemarks = nocData?.Remarks || nocData?.remarks || nocData?.hRemarks || nocData?.HRemarks || remarks || "";
          const isDnarEditable = getFieldStatus("DandARStatus") === "EDITABLE";
          const isPenaltyDateEditable = getFieldStatus("DandARPenaltyEffective") === "EDITABLE";
          const isRemarksEditable = getFieldStatus("Remarks") === "EDITABLE";

          // Module-specific visibility rules matching Mobile Application:
          if (nocType === "NOC_EXINDIA") {
            // In mobile ExIndiaApplication.tsx line 833: {data?.DnARStatus === '' ? null : ( ... )}
            // If D&AR Status is not enabled in enabled_fields_json AND there is no pre-existing D&AR status, hide the card completely.
            const hasDnarValue = Boolean(currentDnarStatus && currentDnarStatus.trim() !== "");
            if (!isDnarEditable && !hasDnarValue) {
              return null;
            }
          }

          // For NOC_PASSPORT, NOC_EXINDIA, or whenever DandARStatus is editable:
          // Render form controls (with fields enabled/disabled per enabled_fields_json).
          // For NOC_OUTJOB when not editable:
          // Render the 3-box summary card (DAR Status, D&AR Penalty Effective, Remarks by Controlling).
          const renderAsForm = nocType === "NOC_PASSPORT" || nocType === "NOC_EXINDIA" || isDnarEditable;

          return (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0">
                  <i className="ti ti-shield-check me-2 text-primary"></i>
                  D&AR status of the employees
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {renderAsForm ? (
                    <>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">
                          D&AR status of the employee:
                          {isDnarEditable && (
                            <span className="text-danger"> *</span>
                          )}
                        </label>
                        <select
                          className="form-select"
                          value={currentDnarStatus}
                          disabled={!isDnarEditable}
                          onChange={(e) => {
                            setDnARStatus(e.target.value);
                            setDnARPenaltyEffective(
                              e.target.value !== "M" && e.target.value !== "J"
                                ? ""
                                : dnARPenaltyEffective
                            );
                          }}
                        >
                          <option value="">Select Status</option>
                          <option value="N">No D&AR contemplated against the employee</option>
                          <option value="P">D&AR Pending/ Deemed Suspension /Suspension/ Court Case Pending</option>
                          <option value="M">D&AR completed; Minor penalty has been imposed</option>
                          <option value="J">D&AR completed; Major penalty has been imposed</option>
                        </select>
                      </div>

                      {(currentDnarStatus === "M" || currentDnarStatus === "J") && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-bold">
                            D&AR Penalty Effective Date
                            {isPenaltyDateEditable && (
                              <span className="text-danger"> *</span>
                            )}
                          </label>
                          <DatePicker
                            className="form-control"
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            placeholder="Select date"
                            disabled={!isPenaltyDateEditable}
                            onChange={(date) => {
                              if (date && date.isValid()) {
                                const formattedDate =
                                  date.format("YYYY-MM-DD");
                                setDnARPenaltyEffective(formattedDate);
                              } else {
                                setDnARPenaltyEffective("");
                              }
                            }}
                            value={
                              penaltyDate &&
                                penaltyDate !== "0000-00-00" &&
                                penaltyDate !== "00000000" &&
                                penaltyDate !== "--" &&
                                penaltyDate !== "-" &&
                                dayjs(penaltyDate).isValid()
                                ? dayjs(penaltyDate)
                                : null
                            }
                          />
                        </div>
                      )}

                      {getFieldStatus("Remarks") !== "HIDDEN" && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-bold">
                            Remarks (if any):
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter remarks (if any)"
                            value={remarks !== "" ? remarks : ((nocData?.Remarks !== "X" && nocData?.Remarks) || (nocData?.remarks !== "X" && nocData?.remarks) || "")}
                            disabled={!isRemarksEditable}
                            onChange={(e) => setRemarks(e.target.value)}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    /* Mobile replica for NOC_OUTJOB: View-only InfoRows when DandARStatus is not editable */
                    <>
                      <div className="col-md-6 col-lg-4">
                        <div className="p-3 bg-light rounded border h-100">
                          <small className="text-muted d-block text-uppercase fw-medium mb-1" style={{ fontSize: "0.78rem", letterSpacing: "0.04em" }}>
                            DAR Status
                          </small>
                          <div className="fw-bold text-dark fs-15">
                            {currentDnarStatus ? formatDarStatus(currentDnarStatus) : "-"}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 col-lg-4">
                        <div className="p-3 bg-light rounded border h-100">
                          <small className="text-muted d-block text-uppercase fw-medium mb-1" style={{ fontSize: "0.78rem", letterSpacing: "0.04em" }}>
                            D&AR Penalty Effective
                          </small>
                          <div className="fw-bold text-dark fs-15">
                            {(() => {
                              if (!penaltyDate || penaltyDate === "0000-00-00" || penaltyDate === "00000000" || penaltyDate === "00.00.0000" || penaltyDate === "-" || penaltyDate === "--" || penaltyDate === "Invalid date") return "-";
                              return formatDate(penaltyDate) || "-";
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 col-lg-4">
                        <div className="p-3 bg-light rounded border h-100">
                          <small className="text-muted d-block text-uppercase fw-medium mb-1" style={{ fontSize: "0.78rem", letterSpacing: "0.04em" }}>
                            Remarks by Controlling
                          </small>
                          <div className="fw-bold text-dark fs-15">
                            {controllingRemarks || "-"}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Final D&AR Vigilance status HR Section */}
        {/* Mobile logic: show ONLY when enabled_fields_fields_json.FinalDandARVigilance === 'V' OR data.FinalDandARVigilance === 'V' */}
        {(nocData?.enabled_fields_fields_json?.FinalDandARVigilance === "V" ||
          nocData?.enabled_fields_json?.FinalDandARVigilance === "V" ||
          nocData?.FinalDandARVigilance === "V") && (
            <div className="card mb-4 border-info">
              <div className="card-header bg-info text-white">
                <h5 className="card-title mb-0 text-white">
                  <i className="ti ti-eye me-2"></i>
                  Final D&AR/Vigilance status checked by HR Department
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-0">
                  <label className="form-label fw-bold">Final D&AR/Vigilance status checked by HR Department</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Write something..."
                    value={finalDandARVigilance || nocData?.finalDandARVigilance || ""}
                    onChange={(e) => setFinalDandARVigilance(e.target.value)}
                    disabled={getFieldStatus("FinalDandARVigilance") === "VIEW_ONLY"}
                  />
                </div>
              </div>
            </div>
          )}

        {/* Standalone HR Document Upload Section (for HR department use only) - matches Mobile condition */}
        {(nocData?.enabled_fields_fields_json?.FinalDandARVigilance === "V" ||
          nocData?.enabled_fields_json?.FinalDandARVigilance === "V" ||
          nocData?.FinalDandARVigilance === "V") && (
            <div className="card mb-4 shadow-sm" style={{ backgroundColor: "#f8f9fa" }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center mb-2">


                  <h6 className="fw-bold mb-0 text-dark">
                    Upload additional documents (for HR department use only):
                  </h6>
                </div>

                <div className="row g-2 align-items-center mb-3">
                  <div className="col-md-4 col-sm-12">
                    <select
                      className="form-select form-select-sm"
                      value={hrDocType}
                      onChange={(e) => setHrDocType(e.target.value)}
                      disabled={hrDocUploading}
                    >
                      <option value="E">Education/Qualifications Certificates</option>
                      <option value="A">Advertisement for Outside job</option>
                      <option value="O">Other</option>
                      <option value="N">NOC and Addition Office Order</option>
                    </select>
                  </div>
                  <div className="col-md-5 col-sm-8">
                    <input
                      type="file"
                      className="form-control form-control-sm"
                      onChange={handleHrFileUpload}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      disabled={hrDocUploading}
                    />
                  </div>
                  <div className="col-md-3 col-sm-4">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary w-100"
                      onClick={uploadOutjobDocuments}
                      disabled={hrUploadedDocs.length === 0 || hrDocUploading}
                    >
                      <i className="ti ti-cloud-upload me-1"></i>
                      UPLOAD
                    </button>
                  </div>
                </div>

                {hrUploadedDocs.length > 0 && (
                  <div className="border rounded bg-white p-2">
                    <div className="fw-bold text-uppercase text-muted mb-2 style-sm" style={{ fontSize: "11px", letterSpacing: "0.05em" }}>
                      DELETE ATTACHMENT
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Uploaded Document</th>
                            <th>File Type</th>
                            <th>File Size (20-50 KB)</th>
                            <th style={{ width: "70px" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hrUploadedDocs.map((doc, idx) => (
                            <tr key={idx}>
                              <td>{doc.fileName}</td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={doc.typeOfAttachedDoc || "O"}
                                  onChange={(e) => {
                                    setHrUploadedDocs((prev) => {
                                      const updated = [...prev];
                                      updated[idx].typeOfAttachedDoc = e.target.value;
                                      return updated;
                                    });
                                  }}
                                >
                                  <option value="E">Education/Qualifications Certificates</option>
                                  <option value="A">Advertisement for Outside job</option>
                                  <option value="O">Other</option>
                                  <option value="N">NOC and Addition Office Order</option>
                                </select>
                              </td>
                              <td>{doc.fileSize || "20-50 KB"}</td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger py-0 px-2"
                                  onClick={() => setHrUploadedDocs((prev) => prev.filter((_, i) => i !== idx))}
                                >
                                  <i className="ti ti-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}





        {/* Declaration & Terms & Conditions Section - For NOC_ONLINECOURSE */}
        {nocType === "NOC_ONLINECOURSE" && (
          <>
            {/* Terms & Conditions Box */}
            <div
              className="card mb-4"
              style={{
                backgroundColor: "#fffbea",
                border: "1px solid #f0e68c",
              }}
            >
              <div
                className="card-header"
                style={{ backgroundColor: "#f0e68c" }}
              >
                <h6 className="mb-0 fw-bold text-dark">
                  Terms & Conditions:
                </h6>
              </div>
              <div className="card-body">
                <ol
                  className="mb-0 text-dark"
                  style={{
                    paddingLeft: "20px",
                    lineHeight: "1.8",
                  }}
                >
                  <li className="mb-2">
                    Claim will be reimbursed after qualifying of the course and submission of copy of Certificate awarded, payment details.
                  </li>
                  <li className="mb-2">
                    Provision for accessories, such as computer & peripherals, laptops, internet connections etc., would not be provided by DMRC and employee undergoing the courses, shall have to arrange the same.
                  </li>
                  <li className="mb-2">
                    The online Courses Should not be pursued during office/duty hours.
                  </li>
                  <li className="mb-2">
                    Any kind of special consideration/leave/transfer shifting of duty etc. to pursue the Online Course, shall not be entertained.
                  </li>
                  <li className="mb-2">
                    No further incentive/s shall be permissible on successful completion of the Online courses.
                  </li>
                  <li className="mb-2">
                    The courses as contained in the approved list of courses shall only be pursued for reimbursement of course fee and addition of qualification in service records. Request(s) for consideration of any other course will not be considered for the above purposes.
                  </li>
                  <li className="mb-2">
                    Employees are advised to avail discount (if offered), by the University/College.
                  </li>
                  <li className="mb-0">
                    An employee can pursue any number of courses, however, fee reimbursement shall be restricted to Rs. 18000/- per block of 3 years(from completion of one course).
                  </li>
                </ol>
              </div>
            </div>

            {/* Declaration Card */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="ti ti-file-check me-2"></i>
                  Declaration
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12">
                    <div className="d-flex align-items-start gap-3">
                      <input
                        type="checkbox"
                        className="form-check-input mt-1"
                        id="declarationCheckbox"
                        checked={declarationAccepted}
                        disabled={true}
                        readOnly={true}
                        onChange={(e) => setDeclarationAccepted(e.target.checked)}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "not-allowed",
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="declarationCheckbox"
                        style={{
                          fontSize: "14px",
                          lineHeight: "1.6",
                          cursor: "default",
                        }}
                      >
                        I hereby declare that the information given above is true
                        and my duty hours in DMRC would not get disturbed in any
                        case. I accept the terms & conditions above. I may kindly
                        be granted permission to enroll for Online Course.
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {nocType === "NOC_EXINDIA" && (
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-checkbox me-2 text-primary"></i>
                Declaration & D&AR Status
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                    <span className="text-muted">
                      Whether any D&AR Pending
                    </span>
                    <span className={`badge ${nocData?.dnARPending == "Y" ? "bg-danger" : "bg-success"}`}>
                      {nocData?.dnARPending == "Y"
                        ? "Yes"
                        : nocData?.dnARPending == "N"
                          ? "No"
                          : "N/A"}
                    </span>
                  </div>

                  <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                    <span className="text-muted">
                      Whether any Vigilance Pending
                    </span>
                    <span className={`badge ${nocData?.vigilancePending == "Y" ? "bg-danger" : "bg-success"}`}>
                      {nocData?.vigilancePending == "Y"
                        ? "Yes"
                        : nocData?.vigilancePending == "N"
                          ? "No"
                          : "N/A"}
                    </span>
                  </div>

                  <div className="d-flex align-items-center justify-content-between">
                    <span className="text-muted">
                      Previously availed Ex-India leave during the last 5 years
                    </span>
                    <span className={`badge ${Array.isArray(nocData?.travelEntries) && nocData.travelEntries.length > 0 ? "bg-primary" : "bg-success"}`}>
                      {Array.isArray(nocData?.travelEntries) && nocData.travelEntries.length > 0
                        ? "Yes"
                        : "No"}
                    </span>
                  </div>

                  {Array.isArray(nocData?.travelEntries) && nocData.travelEntries.length > 0 && (
                    <div className="table-responsive mt-3">
                      <table className="table table-bordered table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Country</th>
                            <th>Duration of Visit</th>
                            <th>Purpose of Visit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {nocData.travelEntries.map((entry: any, index: number) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{entry?.Country || entry?.country || "-"}</td>
                              <td>{entry?.DurationOfVisit || entry?.durationOfVisit || "-"}</td>
                              <td>{entry?.PurposeOfVisit || entry?.purposeOfVisit || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Chat Section - Communication */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Communication</h5>
              <button
                className="btn btn-primary btn-sm position-relative"
                onClick={handleChatToggle}
              >
                <i className="ti ti-message me-1"></i>
                {showChat ? "Close Chat" : "Start Chat"}
                {unreadCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Chat Drawer */}
        {showChat && (
          <>
            {/* Chat Widget Docked to Bottom-Right Corner */}
            <div
              className="position-fixed bg-white chat-corner-widget shadow-lg d-flex flex-column m-3 border"
              style={{
                bottom: 0,
                right: 0,
                width: "400px",
                height: "600px",
                maxHeight: "85vh",
                zIndex: 1060,
                borderRadius: "4px",
              }}
            >
              {/* Chat Header */}
              <div className="d-flex align-items-center justify-content-between px-3 py-2.5 border-bottom bg-white flex-shrink-0 chat-widget-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="position-relative">
                    <div
                      className="avatar bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center fw-bold"
                      style={{ width: "34px", height: "34px" }}
                    >
                      <i className="ti ti-messages fs-16"></i>
                    </div>
                    <span
                      className="position-absolute bottom-0 end-0 bg-success rounded-circle border border-2 border-white"
                      style={{ width: "9px", height: "9px" }}
                    />
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-1.5">
                      <h6 className="mb-0 fw-bold text-dark fs-13">Chat & Communication</h6>
                      <span className="badge bg-success-subtle text-success fs-9 px-1.5 py-0.5 rounded-pill fw-medium">
                        Active
                      </span>
                    </div>
                    <small className="text-muted fs-11 d-block text-truncate" style={{ maxWidth: "200px" }}>
                      NOC Task • Ref: #{nocData?.referenceNumber || nocData?.id || ""}
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-circle text-muted chat-header-btn"
                    style={{ width: "28px", height: "28px" }}
                    onClick={() => {
                      if (nocData?.id) {
                        dispatch(fetchMessages({ nocRequestId: nocData.id }));
                        showToast("Info", "Messages refreshed", "primary");
                      }
                    }}
                    disabled={chatLoading}
                    title="Refresh Messages"
                  >
                    <i className={`ti ti-refresh fs-12 ${chatLoading ? "ti-spin text-primary" : ""}`}></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-circle text-muted chat-header-btn"
                    style={{ width: "28px", height: "28px" }}
                    onClick={handleChatToggle}
                    title="Close"
                  >
                    <i className="ti ti-x fs-14"></i>
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="chat-messages-container p-3 flex-grow-1 overflow-auto" style={{ backgroundColor: "#f8fafc" }}>
                {(chatError || uploadError) && (
                  <div className="alert alert-danger alert-dismissible py-2 px-3 fs-12 mb-2 rounded-3 shadow-xs">
                    <button
                      type="button"
                      className="btn-close fs-10"
                      onClick={() => {
                        dispatch(clearError());
                        dispatch(clearUploadError());
                      }}
                    ></button>
                    {chatError || uploadError}
                  </div>
                )}

                {chatLoading && messages.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted small mt-2 fs-12">Loading conversation...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-5 my-2 text-muted">
                    <div
                      className="avatar avatar-md bg-white rounded-circle shadow-xs mx-auto mb-2 d-flex align-items-center justify-content-center text-primary"
                      style={{ width: 44, height: 44 }}
                    >
                      <i className="ti ti-messages fs-20"></i>
                    </div>
                    <h6 className="fw-semibold text-dark fs-13 mb-1">No Messages Yet</h6>
                    <p className="small text-muted mb-0 fs-11">Send a message below to start the conversation.</p>
                  </div>
                ) : (
                  messages.map((msg: ChatMessage, index: number) => {
                    const isOwn = isMyMessage(msg);
                    const rawMsg = msg.message || "";
                    const paperclipIdx = rawMsg.indexOf("📎");
                    const hasPaperclip = paperclipIdx !== -1;
                    const displayMsgText = hasPaperclip
                      ? rawMsg.substring(0, paperclipIdx).trim()
                      : rawMsg.trim();
                    let extractedAttNames: string[] = [];
                    if (hasPaperclip) {
                      const attPart = rawMsg.substring(paperclipIdx).replace(/^[📎\s]+/, "").trim();
                      extractedAttNames = attPart.split(",").map((s: string) => s.trim()).filter(Boolean);
                    }
                    const attachmentsList: any[] =
                      msg.attachments && msg.attachments.length > 0
                        ? msg.attachments
                        : extractedAttNames.length > 0
                          ? extractedAttNames
                          : [];

                    return (
                      <div
                        key={msg.id || index}
                        className={`d-flex mb-3 ${isOwn ? "justify-content-end" : "justify-content-start"}`}
                      >
                        {/* Participant Avatar */}
                        {!isOwn && (
                          <div className="me-1.5 flex-shrink-0 align-self-end mb-1">
                            <EmployeeAvatar
                              empName={msg.senderName || "Participant"}
                              empId={msg.senderEmpId}
                              size="sm"
                              showName={false}
                              showEmpId={false}
                              className="rounded-circle shadow-xs"
                            />
                          </div>
                        )}

                        <div
                          className="position-relative"
                          style={{ maxWidth: "80%", wordBreak: "break-word" }}
                        >
                          {/* Sender Label Above Bubble */}
                          <div
                            className={`d-flex align-items-center gap-1.5 mb-1 px-1 ${isOwn ? "justify-content-end" : "justify-content-start"
                              }`}
                          >
                            <span className="fw-semibold text-dark fs-11">
                              {isOwn ? "You" : (msg.senderName || "Participant")}
                            </span>
                            <span
                              className={`badge fs-9 px-1.5 py-0.2 text-capitalize rounded-pill ${isOwn
                                ? "bg-primary-subtle text-primary"
                                : msg.senderType === "approver"
                                  ? "bg-warning-subtle text-warning-emphasis"
                                  : "bg-secondary-subtle text-secondary"
                                }`}
                            >
                              {msg.senderType || (isOwn ? "user" : "system")}
                            </span>
                          </div>

                          <div
                            className={`p-3 shadow-sm position-relative rounded-1 ${isOwn
                              ? "bg-primary text-white"
                              : "bg-white text-dark border"
                              }`}
                          >
                            {msg.documentType && (
                              <div className="mb-1.5">
                                <span
                                  className={`badge fs-10 fw-medium px-2 py-0.5 rounded-pill ${isOwn
                                    ? "bg-white bg-opacity-25 text-white"
                                    : "bg-warning-subtle text-warning-emphasis border border-warning"
                                    }`}
                                >
                                  <i className="ti ti-file-certificate me-1"></i>
                                  Requested: {msg.documentType}
                                </span>
                              </div>
                            )}

                            {displayMsgText && (
                              <p
                                className="mb-0 chat-message-content"
                                style={{
                                  fontSize: "13px",
                                  lineHeight: "1.45",
                                  color: isOwn ? "#ffffff" : "#1e293b",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {displayMsgText}
                              </p>
                            )}

                            {/* Render Interactive Attachments / Images */}
                            {attachmentsList.length > 0 && (
                              <div
                                className={`d-flex flex-column gap-1.5 ${displayMsgText ? "mt-2 pt-2 border-top" : ""
                                  }`}
                                style={{
                                  borderColor: isOwn ? "rgba(255, 255, 255, 0.2)" : "#f1f5f9",
                                }}
                              >
                                {attachmentsList.map((attachment: any, attIdx: number) => {
                                  const rawName =
                                    typeof attachment === "string"
                                      ? attachment
                                      : attachment.fileName ||
                                      attachment.name ||
                                      attachment.FileName ||
                                      "Attachment";
                                  const fileName =
                                    rawName.split("/").pop()?.split("-").slice(2).join("-") ||
                                    rawName.split("/").pop() ||
                                    "Attachment";
                                  const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(fileName);
                                  const isPdf = /\.pdf$/i.test(fileName);
                                  const fileSize = typeof attachment === "object" ? attachment.fileSize : undefined;

                                  return (
                                    <div
                                      key={attIdx}
                                      className="d-flex align-items-center justify-content-between rounded-1 shadow-xs attachment-card cursor-pointer"
                                      onClick={() => handleAttachmentClick(attachment)}
                                      style={{
                                        cursor: "pointer",
                                        borderRadius: "5px",
                                        transition: "all 0.15s ease",
                                        backgroundColor: "#ffffff",
                                        border: "1px solid #e2e8f0",
                                        padding: "4px 6px",
                                        maxWidth: "235px",
                                      }}
                                      title="Click to view / open attachment"
                                    >
                                      <div className="d-flex align-items-center overflow-hidden me-1.5" style={{ minWidth: 0 }}>
                                        <div
                                          className="rounded-1 d-flex align-items-center justify-content-center flex-shrink-0 me-1.5"
                                          style={{
                                            width: "24px",
                                            height: "24px",
                                            borderRadius: "4px",
                                            backgroundColor: isPdf
                                              ? "#fee2e2"
                                              : isImg
                                                ? "#dbeafe"
                                                : "#f1f5f9",
                                            color: isPdf
                                              ? "#dc2626"
                                              : isImg
                                                ? "#2563eb"
                                                : "#475569",
                                          }}
                                        >
                                          <i className={`${isImg ? "ti ti-photo" : isPdf ? "ti ti-file-text" : "ti ti-paperclip"} fs-13`}></i>
                                        </div>
                                        <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                          <span
                                            className="text-truncate fw-semibold d-block text-decoration-underline"
                                            style={{
                                              color: "#0f172a",
                                              fontSize: "11px",
                                              maxWidth: "145px",
                                              lineHeight: "1.2",
                                            }}
                                          >
                                            {fileName}
                                          </span>
                                          <small
                                            className="d-block text-truncate"
                                            style={{
                                              fontSize: "9px",
                                              color: "#64748b",
                                              lineHeight: "1.1",
                                              marginTop: "1px",
                                            }}
                                          >
                                            {fileSize ? `${fileSize} • ` : ""}
                                            {isImg ? "Image" : isPdf ? "PDF" : "File"}
                                          </small>
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-icon rounded-1 p-0 d-flex align-items-center justify-content-center"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAttachmentClick(attachment);
                                          }}
                                          style={{
                                            width: "21px",
                                            height: "21px",
                                            borderRadius: "3px",
                                            color: "#334155",
                                            border: "1px solid #e2e8f0",
                                            background: "#f8fafc",
                                          }}
                                          title="Open in External Tab"
                                        >
                                          <i className="ti ti-external-link" style={{ fontSize: "11px" }}></i>
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-icon rounded-1 p-0 d-flex align-items-center justify-content-center"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            downloadChatAttachment(attachment, nocData?.id, nocType, dispatch, attachments);
                                          }}
                                          style={{
                                            width: "21px",
                                            height: "21px",
                                            borderRadius: "3px",
                                            color: "#334155",
                                            border: "1px solid #e2e8f0",
                                            background: "#f8fafc",
                                          }}
                                          title="Download Attachment"
                                        >
                                          <i className="ti ti-download" style={{ fontSize: "11px" }}></i>
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Timestamp */}
                            <div className="text-end mt-1">
                              <small
                                style={{
                                  fontSize: "9.5px",
                                  color: isOwn ? "rgba(255, 255, 255, 0.75)" : "#94a3b8",
                                }}
                              >
                                {formatMessageTime(msg.createdAt)}
                              </small>
                            </div>
                          </div>
                        </div>

                        {/* Own Avatar */}
                        {isOwn && (
                          <div className="ms-1.5 flex-shrink-0 align-self-end mb-1">
                            <EmployeeAvatar
                              empName={msg.senderName || "User"}
                              empImage=""
                              empId={msg.senderEmpId || nocData?.empID}
                              size="sm"
                              showName={false}
                              showEmpId={false}
                              className="rounded-circle shadow-xs"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <div className="chat-input-container bg-white border-top p-2.5 flex-shrink-0">
                {/* File Attachments Preview */}
                {selectedFiles.length > 0 && (
                  <div className="mb-2 file-attachment-preview">
                    <div className="d-flex flex-wrap gap-1.5">
                      {selectedFiles.map((file, index) => {
                        const fileSizeKB = (file.size / 1024).toFixed(1);
                        const isUploading = uploadingFiles.has(index);

                        return (
                          <div
                            key={index}
                            className="d-flex align-items-center bg-light border rounded-pill px-2.5 py-1 shadow-xs"
                          >
                            <i className="ti ti-file-text text-primary me-1.5 fs-12"></i>
                            <span
                              className="small text-truncate fw-medium text-dark me-1 fs-11"
                              style={{ maxWidth: "120px" }}
                            >
                              {file.name}
                            </span>
                            <span className="text-muted fs-10 me-1.5">({fileSizeKB}KB)</span>

                            {isUploading ? (
                              <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 12, height: 12 }}>
                                <span className="visually-hidden">Uploading...</span>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-xs btn-success rounded-circle p-0 d-flex align-items-center justify-content-center"
                                  onClick={() => confirmFileUpload(index)}
                                  title="Send file"
                                  style={{ width: "18px", height: "18px" }}
                                >
                                  <i className="ti ti-check fs-9"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-danger rounded-circle p-0 d-flex align-items-center justify-content-center"
                                  onClick={() => removeFile(index)}
                                  title="Remove"
                                  style={{ width: "18px", height: "18px" }}
                                >
                                  <i className="ti ti-x fs-9"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="d-flex align-items-center gap-1.5">
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelection}
                    multiple
                    className="d-none"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  />

                  {/* Attachment Button */}
                  <button
                    type="button"
                    className="btn btn-light rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 chat-attach-btn"
                    onClick={triggerFileInput}
                    disabled={uploading}
                    style={{ width: "36px", height: "36px" }}
                    title="Attach files"
                  >
                    {uploading ? (
                      <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14 }}>
                        <span className="visually-hidden">...</span>
                      </div>
                    ) : (
                      <i className="ti ti-paperclip fs-16 text-muted"></i>
                    )}
                  </button>

                  <div className="flex-grow-1 position-relative">
                    <input
                      type="text"
                      className="form-control rounded-pill px-3.5 py-2 fs-13 border chat-input-field"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      disabled={uploading}
                      style={{ backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}
                    />
                  </div>

                  <button
                    className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 shadow chat-send-btn"
                    onClick={handleSendMessage}
                    disabled={uploading || (!newMessage.trim() && selectedFiles.length === 0)}
                    style={{
                      width: "36px",
                      height: "36px",
                      background: "linear-gradient(135deg, #f26522 0%, #d94e0c 100%)",
                      border: "none",
                    }}
                    title="Send message"
                  >
                    {uploading ? (
                      <div className="spinner-border spinner-border-sm text-white" role="status" style={{ width: 14, height: 14 }}>
                        <span className="visually-hidden">...</span>
                      </div>
                    ) : (
                      <i className="ti ti-send text-white fs-14"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Media / Lightbox Preview Modal */}
        {previewMedia && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center"
            style={{ zIndex: 1080, backdropFilter: "blur(4px)" }}
            onClick={() => setPreviewMedia(null)}
          >
            <div
              className="bg-white rounded-4 shadow-lg p-3 position-relative"
              style={{ maxWidth: "90vw", maxHeight: "90vh", minWidth: "320px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                <h6 className="mb-0 fw-bold text-dark text-truncate fs-14" style={{ maxWidth: "70%" }}>
                  <i className="ti ti-photo me-1.5 text-primary"></i>
                  {previewMedia.title}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <a
                    href={previewMedia.url}
                    target="_blank"
                    rel="noreferrer"
                    download={previewMedia.title}
                    className="btn btn-sm btn-outline-primary py-1 px-2 fs-12"
                  >
                    <i className="ti ti-download me-1"></i>Download
                  </a>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setPreviewMedia(null)}
                  ></button>
                </div>
              </div>
              <div className="text-center overflow-auto p-2" style={{ maxHeight: "calc(90vh - 100px)" }}>
                {previewMedia.isImage ? (
                  <img
                    src={previewMedia.url}
                    alt={previewMedia.title}
                    className="img-fluid rounded"
                    style={{ maxHeight: "75vh", objectFit: "contain" }}
                  />
                ) : (
                  <iframe
                    src={previewMedia.url}
                    title={previewMedia.title}
                    style={{ width: "80vw", height: "70vh", border: "none" }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Document Type Selection Dialog */}
        {showDocumentTypeDialog && (
          <>
            {/* Backdrop */}
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
              style={{ zIndex: 1065, backdropFilter: "blur(2px)" }}
              onClick={handleCancelDocumentType}
            />

            {/* Dialog */}
            <div
              className="position-fixed top-50 start-50 translate-middle bg-white rounded-4 shadow-lg"
              style={{ zIndex: 1070, minWidth: "400px", maxWidth: "90vw" }}
            >
              <div className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-file-text me-2 text-primary"></i>
                    Select Document Type
                  </h5>
                  <button
                    className="btn-close"
                    onClick={handleCancelDocumentType}
                  ></button>
                </div>

                <p className="text-muted mb-3 fs-13">
                  Which type of document are you requesting from the employee?
                </p>

                <div className="mb-3">
                  <div className="form-check mb-2 p-3 bg-light rounded-3 border">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="documentType"
                      id="docTypeEducation"
                      value="Education"
                      checked={selectedDocumentType === "Education"}
                      onChange={(e) => setSelectedDocumentType(e.target.value)}
                    />
                    <label
                      className="form-check-label ms-2"
                      htmlFor="docTypeEducation"
                    >
                      <span className="fw-semibold text-dark d-block">Education Documents</span>
                      <small className="text-muted">Certificates, degrees, marksheets</small>
                    </label>
                  </div>

                  <div className="form-check mb-2 p-3 bg-light rounded-3 border">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="documentType"
                      id="docTypeAdvertisement"
                      value="Advertisement"
                      checked={selectedDocumentType === "Advertisement"}
                      onChange={(e) => setSelectedDocumentType(e.target.value)}
                    />
                    <label
                      className="form-check-label ms-2"
                      htmlFor="docTypeAdvertisement"
                    >
                      <span className="fw-semibold text-dark d-block">Advertisement Documents</span>
                      <small className="text-muted">Job vacancy circulars, clippings</small>
                    </label>
                  </div>

                  <div className="form-check mb-2 p-3 bg-light rounded-3 border">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="documentType"
                      id="docTypeOthers"
                      value="Others"
                      checked={selectedDocumentType === "Others"}
                      onChange={(e) => setSelectedDocumentType(e.target.value)}
                    />
                    <label className="form-check-label ms-2" htmlFor="docTypeOthers">
                      <span className="fw-semibold text-dark d-block">Other Documents</span>
                      <small className="text-muted">Any other supporting files</small>
                    </label>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-light"
                    onClick={handleCancelDocumentType}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleDocumentTypeSelection}
                    disabled={!selectedDocumentType}
                  >
                    <i className="ti ti-check me-1"></i>
                    Send Request
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Add required CSS styles */}
        <style>
          {`
            @keyframes chatSlideIn {
              from {
                transform: translateY(20px) scale(0.96);
                opacity: 0;
              }
              to {
                transform: translateY(0) scale(1);
                opacity: 1;
              }
            }
            
            .chat-corner-widget {
              bottom: 24px;
              right: 28px;
              width: 390px;
              max-width: calc(100vw - 32px);
              height: 560px;
              max-height: calc(100vh - 48px);
              border-radius: 18px;
              box-shadow: 0 20px 50px -10px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(0, 0, 0, 0.06);
              overflow: hidden;
              animation: chatSlideIn 0.22s cubic-bezier(0.16, 1, 0.3, 1);
            }
            
            @media (max-width: 576px) {
              .chat-corner-widget {
                bottom: 0 !important;
                right: 0 !important;
                left: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                height: 85vh !important;
                max-height: 85vh !important;
                border-radius: 20px 20px 0 0 !important;
              }
            }
            
            .chat-bubble-incoming {
              border-radius: 4px 16px 16px 16px;
              border: 1px solid #e2e8f0;
              background-color: #ffffff;
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
            }

            .chat-bubble-outgoing {
              border-radius: 16px 4px 16px 16px;
              background: linear-gradient(135deg, #f26522 0%, #d94e0c 100%);
              box-shadow: 0 4px 14px rgba(242, 101, 34, 0.25);
            }

            .chat-header-btn:hover {
              background-color: #f1f5f9 !important;
              color: #0f172a !important;
            }

            .chat-attach-btn:hover {
              background-color: #e2e8f0 !important;
            }

            .chat-send-btn:hover:not(:disabled) {
              transform: translateY(-1px);
              box-shadow: 0 6px 16px rgba(242, 101, 34, 0.35) !important;
            }

            .chat-input-field:focus {
              background-color: #ffffff !important;
              border-color: #f26522 !important;
              box-shadow: 0 0 0 3px rgba(242, 101, 34, 0.15) !important;
            }

            .chat-messages-container {
              flex: 1;
              overflow-y: auto;
              min-height: 0;
            }
            
            .chat-input-container {
              flex-shrink: 0;
            }
            
            .chat-message-content {
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
            }
            
            .file-attachment-preview {
              max-width: 100%;
              overflow-x: auto;
            }
          `}
        </style>

        {/* Approver Remarks & Action Card (Level 2+ / Rejection) */}
        {nocType !== "NOC_ONLINECOURSE-REIMB" &&
          (nocType?.startsWith("NOC_") ||
            nocData?.enabled_fields_fields_json?.ReasonForRejection === 'X' ||
            nocData?.enabled_fields_json?.ReasonForRejection === 'X' ||
            nocData?.enabled_fields_fields_json?.Remarks1 === 'X' ||
            nocData?.enabled_fields_json?.Remarks1 === 'X' ||
            nocData?.enabled_fields_json?.HRemarks === 'X' ||
            nocType === "NOC_HIGHERSTUDY" ||
            nocType === "NOC_ONLINECOURSE") && (
            <div className="card mb-4 border-primary-subtle shadow-sm">
              <div className="card-header bg-light d-flex align-items-center">
                <h5 className="card-title mb-0 text-primary">
                  <i className="ti ti-message-dots me-2"></i>
                  Approver Remarks & Action
                </h5>
              </div>
              <div className="card-body">
                {/* Reason for Rejection dropdown */}
                {nocType === "NOC_OUTJOB" &&
                  (nocData?.enabled_fields_fields_json?.ReasonForRejection === "X" ||
                    nocData?.enabled_fields_json?.ReasonForRejection === "X") &&
                  rejectionReasons.length > 0 && (
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Reason for Rejection <span className="text-muted fw-normal">(Required if rejecting)</span>
                      </label>
                      <select
                        className="form-select"
                        value={selectedRejectionReason}
                        onChange={(e) => setSelectedRejectionReason(e.target.value)}
                      >
                        <option value="">Select Reason for Rejection...</option>
                        {rejectionReasons.map((reason: any) => (
                          <option key={reason.id || reason.reasonForRejection} value={reason.reasonForRejection || reason.id}>
                            {reason.description || reason.reasonForRejection}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                {/* Approver Remarks Textarea for HOD / Final Approver (HRemarks / Remarks1) */}
                {(Boolean(nocType) ||
                  nocData?.enabled_fields_fields_json?.Remarks1 === 'X' ||
                  nocData?.enabled_fields_json?.Remarks1 === 'X' ||
                  nocData?.enabled_fields_json?.HRemarks === 'X') && (
                    <div className="mb-0">
                      <label className="form-label fw-bold">
                        Your Remarks
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Write Something..."
                        value={hRemarks}
                        onChange={(e) => setHRemarks(e.target.value)}
                      />
                    </div>
                  )}
              </div>
            </div>
          )}

        {/* Action Buttons */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-success"
                onClick={() => handleApproveReject("A")}
                disabled={isProcessing}
              >
                {isProcessing && processingAction === "A" ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ti ti-check me-2"></i>
                    Approve
                  </>
                )}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleApproveReject("R")}
                disabled={isProcessing}
              >
                {isProcessing && processingAction === "R" ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ti ti-x me-2"></i>
                    Reject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Condition Note for NOC_HIGHERSTUDY */}
        {nocType === "NOC_HIGHERSTUDY" && (
          <div className="alert alert-info border-0 text-center py-2.5 px-3 mb-4 small fw-medium rounded" style={{ backgroundColor: "#f0f6ff", color: "#084298", fontSize: "0.85rem" }}>
            <i className="ti ti-info-circle me-1.5 fs-15"></i>
            *Condition of 03 years Regular Service in DMRC dispensed away with (as per Master Circular).
          </div>
        )}

        {/* Toast Notification */}
        <Toasts
          show={toast.show}
          title={toast.title}
          message={toast.message}
          type={toast.type}
        />
      </div>
    </div>
  );
};

export default TaskDetailsPage;
